/**
 * Deck viewer for the Slide-Deck Creator agent.
 *
 *   #runs/<runId>/deck  →  this component
 *
 * Lists every slide of a deck (split out at render time into the
 * deck_slides table), with an inline comment thread + a "regenerate"
 * button per slide that re-writes just that slide using the open
 * comments + optional notes as the rewrite prompt.
 *
 * Polling: we re-fetch every 4s while any slide is in "regenerating"
 * status, otherwise we stay on the latest manual fetch.
 */
import React from 'react';
import { MarkdownView } from './orgrepo-news-reports-arch.jsx';
import { SlideEditor } from './deck-editor.jsx';
import { renderSlideV2 } from '../lib/deck-v2.js';
import { composeDeckV2 } from '../lib/compose-deck.js';
import { HumanAnswerForm } from './agent-run-board.jsx';
import { useSubCrumbs } from './route-title-context.jsx';
import { publicOrigin } from '../lib/public-origin.ts';
import { exportShellUrl, withStream } from '../lib/export-shell.ts';
import { listPeople } from '../api.js';

// Default agent id per viewer route — used when the URL doesn't carry
// an ?agent=<id> query string. Mirrors the slug→agent map in
// live-chat.jsx's extractAgentRunRefs so the empty-state polling lands
// on the right endpoint.
const DEFAULT_DECK_AGENT_ID = 'agent-slide-deck-creator';

// ── md-first ("Slide creator 2") decks ─────────────────────────────
// These are composed client-side (compose-deck → a V2 spec array) and
// handed off to this same viewer via mode="v2". They live in localStorage
// keyed by a generated id, so the viewer URL (#…/v2-<id>/deck) can resolve
// them without an agent run behind it.
const V2_DECK_PREFIX = 'slidecreator.v2deck.';
function readV2Deck(id) {
  try { return JSON.parse(localStorage.getItem(V2_DECK_PREFIX + id) || 'null'); } catch { return null; }
}
function writeV2Deck(id, deck) {
  try { localStorage.setItem(V2_DECK_PREFIX + id, JSON.stringify(deck)); } catch { /* quota — ignore */ }
}
// Best-effort title for a V2 slide spec (headline / hero / first text).
function v2SlideTitle(s) {
  const zones = s?.content?.zones || {};
  const all = Object.values(zones).flat();
  const hit = all.find(p => p?.content && (p.content.headline || p.content.text));
  return (hit && (hit.content.headline || hit.content.text)) || s?.template || 'Slide';
}

/* Pre-slides empty state: the run exists but slide-designer hasn't
 * written any rows to deck_slides yet. We fetch the agent_run + its
 * steps from the standard run-detail endpoint and render:
 *   - a compact step trace (so the user sees progress)
 *   - the human-approval form (briefing review) when status is
 *     awaiting-human, so the user can approve without bouncing back
 *     to the chat thread
 *   - the latest step's output as markdown when nothing is gated
 *     (the briefing / storyline lives in resultText)
 * Polls every 2.5 s while the run is still in flight. */
function PreSlidesPanel({ runId, agentId }) {
  const [data, setData] = React.useState(null);
  const [err, setErr] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await fetch(`/api/agents/${encodeURIComponent(agentId)}/runs/${encodeURIComponent(runId)}`, { credentials: 'same-origin' });
        if (!res.ok) {
          if (!cancelled) setErr(`run lookup → ${res.status}`);
          return;
        }
        const json = await res.json();
        if (!cancelled) { setData(json); setErr(null); }
      } catch (e) {
        if (!cancelled) setErr((e && e.message) || String(e));
      }
    };
    tick();
    const timer = setInterval(tick, 2500);
    return () => { cancelled = true; clearInterval(timer); };
  }, [runId, agentId]);

  if (err) {
    return (
      <div className="kpi" style={{textAlign:'center'}}>
        <div className="kpi__label">No slides yet</div>
        <p style={{marginTop:'var(--space-2)',color:'var(--fg-muted)'}}>{err}</p>
      </div>
    );
  }
  if (!data?.run) {
    return (
      <div className="kpi" style={{textAlign:'center'}}>
        <div className="kpi__label">Loading run…</div>
      </div>
    );
  }

  const steps = Array.isArray(data.steps) ? data.steps : [];
  const awaitingStep = steps.find(s => s.status === 'awaiting-human') || null;
  const runningStep = steps.find(s => s.status === 'running') || null;
  const lastDone = [...steps].reverse().find(s => s.status === 'done' && s.resultText) || null;
  const runStatus = data.run.status;
  const headerLabel =
    runStatus === 'awaiting-human' ? 'Awaiting your approval'
    : runStatus === 'failed' ? 'Run failed'
    : runStatus === 'done' ? 'Run complete (no slides written)'
    : runningStep ? `${runningStep.name || runningStep.skillId || 'Working'}…`
    : 'Building your deck…';

  const stepDot = (s) => {
    if (s.status === 'done') return { bg:'#DCF1E2', fg:'#00702A', ch:'✓' };
    if (s.status === 'awaiting-human') return { bg:'#FFE9D9', fg:'#A2400D', ch:'⏸' };
    if (s.status === 'failed') return { bg:'#FCE2EA', fg:'#A20235', ch:'✕' };
    if (s.status === 'running') return { bg:'#E6E0F4', fg:'#3D2B6E', ch:'•' };
    return { bg:'var(--bg-alt)', fg:'var(--fg-muted)', ch:'•' };
  };

  return (
    <div style={{
      margin:'0 auto',
      maxWidth:780,
      padding:'18px 20px',
      background:'var(--bg-alt)',
      border:'1px solid var(--border)',
      borderRadius:12,
    }}>
      <div style={{fontSize:13,fontWeight:600,color:'var(--fg-muted)',letterSpacing:'0.02em',textTransform:'uppercase',marginBottom:12}}>
        {headerLabel}
      </div>

      <div style={{display:'flex',flexDirection:'column',gap:6,marginBottom:awaitingStep || lastDone ? 18 : 0}}>
        {steps.map(s => {
          const d = stepDot(s);
          return (
            <div key={s.id} style={{display:'flex',gap:10,alignItems:'flex-start',fontSize:13}}>
              <div style={{
                width:18,height:18,borderRadius:'50%',
                background:d.bg,color:d.fg,fontSize:11,fontWeight:700,
                display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,marginTop:2,
              }}>{d.ch}</div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontWeight:600}}>{s.name || s.skillId || s.type}</div>
                {s.description && <div style={{color:'var(--fg-muted)',fontSize:12,marginTop:1}}>{String(s.description).slice(0, 220)}</div>}
              </div>
            </div>
          );
        })}
      </div>

      {/* The reviewed content (briefing / storyline) — shown both while the
          approval gate is open (so the user reads what they're approving or
          changing) and after, as the latest step output. */}
      {lastDone?.resultText && (
        <div style={{marginTop:6, marginBottom: awaitingStep ? 14 : 0}}>
          <div style={{fontSize:11.5,color:'var(--fg-muted)',fontWeight:600,letterSpacing:'0.04em',textTransform:'uppercase',marginBottom:6}}>
            {awaitingStep ? 'Storyline for your review' : `Latest step output — ${lastDone.name || lastDone.skillId}`}
          </div>
          <div className="md-body" style={{fontSize:14,lineHeight:1.55,maxHeight:480,overflowY:'auto',padding:'12px 14px',background:'var(--bg)',border:'1px solid var(--border)',borderRadius:8}}>
            <MarkdownView source={String(lastDone.resultText).slice(0, 12000)}/>
          </div>
        </div>
      )}

      {awaitingStep && (
        <div style={{marginTop:6}}>
          <HumanAnswerForm
            agentId={agentId}
            runId={runId}
            step={awaitingStep}
            reviewContent={lastDone?.resultText || null}
            onAnswered={() => { /* the 2.5 s poller picks up the status flip */ }}
          />
        </div>
      )}
    </div>
  );
}

// URL helpers — pick the agent-keyed or chat-keyed surface based
// on viewer mode. Both surfaces expose the same JSON shape (GET
// returns { run, slides }; POST accepts comment/resolve/regenerate
// and returns the same fields), so the component below doesn't
// need to branch beyond URL construction.
function deckBase(mode, id) {
  return mode === 'chat'
    ? `/api/chat-artefacts/${encodeURIComponent(id)}/deck`
    : `/api/agents/_/runs/${encodeURIComponent(id)}/deck`;
}
function previewUrl(mode, id) {
  return mode === 'chat'
    ? `/api/chat-artefacts/${encodeURIComponent(id)}/deck/preview`
    : `/api/agents/_/runs/${encodeURIComponent(id)}/deck/preview`;
}
function exportPdfUrl(mode, id) {
  return mode === 'chat'
    ? `/api/chat-artefacts/${encodeURIComponent(id)}/deck/export/pdf`
    : `/api/agents/_/runs/${encodeURIComponent(id)}/deck/export/pdf`;
}
function exportPptxUrl(mode, id) {
  return mode === 'chat'
    ? `/api/chat-artefacts/${encodeURIComponent(id)}/deck/export/pptx`
    : `/api/agents/_/runs/${encodeURIComponent(id)}/deck/export/pptx`;
}

async function deckGet(mode, id) {
  const res = await fetch(deckBase(mode, id), { credentials: 'same-origin' });
  const j = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(j.error || `deck → ${res.status}`);
  return j;
}

async function deckPost(mode, id, payload) {
  const res = await fetch(deckBase(mode, id), {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const j = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(j.error || `deck POST → ${res.status}`);
  return j;
}

function fmt(t) {
  if (!t) return '—';
  return new Date(t).toLocaleString();
}

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function renderSlideMarkdown(md) {
  // Light renderer — the slide markdown shape is:
  //   ## Slide N — title
  //   **Layout:** ...
  //   **Bullets:** ...
  //   - ...
  //   **Speaker notes:** ...
  //   **Visual cue:** ...
  // Display it as a styled box. We don't run a full markdown engine
  // here — the slide block is small and we want a predictable shape.
  const lines = md.split('\n');
  const out = [];
  let bullets = null;
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) { if (bullets) { out.push(['ul', bullets]); bullets = null; } continue; }
    if (/^##\s+Slide\s/i.test(line)) {
      out.push(['h', line.replace(/^##\s+/, '')]);
    } else if (/^\*\*Layout:\*\*/i.test(line)) {
      out.push(['kv', 'Layout', line.replace(/^\*\*Layout:\*\*/i, '').trim()]);
    } else if (/^\*\*Bullets:\*\*/i.test(line)) {
      bullets = [];
    } else if (/^\*\*Speaker notes:\*\*/i.test(line)) {
      if (bullets) { out.push(['ul', bullets]); bullets = null; }
      out.push(['notes', line.replace(/^\*\*Speaker notes:\*\*/i, '').trim()]);
    } else if (/^\*\*Visual cue:\*\*/i.test(line)) {
      if (bullets) { out.push(['ul', bullets]); bullets = null; }
      out.push(['kv', 'Visual cue', line.replace(/^\*\*Visual cue:\*\*/i, '').trim()]);
    } else if (line.startsWith('- ')) {
      if (!bullets) bullets = [];
      bullets.push(line.slice(2));
    } else {
      if (bullets) { bullets.push(line); }
      else { out.push(['p', line]); }
    }
  }
  if (bullets) out.push(['ul', bullets]);
  return out;
}

/**
 * Renders one designed slide HTML inside a sandboxed iframe at the
 * Eraneos design system's native 1920×1080 canvas, scaled to fit
 * the carousel column. The slide-fit.js script inside the iframe
 * handles any internal scaling (cover-fit within the iframe
 * viewport); this outer scale just shrinks the iframe DOM element
 * proportionally to the wrapper width.
 *
 * sandbox attribute is restrictive on purpose — the html came from
 * Claude and we treat it as untrusted content. allow-same-origin
 * is set so the iframe's <link rel="stylesheet" href="/brand/...">
 * + the slide-fit.js loader can pull from the parent origin.
 */
// Hit-test a pin click against the slide's DOM so a comment can name the
// element it lands on (e.g. `title "Accelerate growth"`), not just a
// region. The iframe is authored at a fixed 1920×1080 and (in the viewer)
// slide-fit's cover pass is a no-op at that size, so the iframe-local pixel
// coords map straight through. Best-effort: returns null when the content
// can't be inspected or nothing meaningful resolves — coordinates still work.
const SLIDE_ROLES = [
  [/slide__title|cover__title/, 'title'],
  [/slide__eyebrow|slide__kicker/, 'eyebrow label'],
  [/slide__preamble|slide__lede|slide__subtitle/, 'subtitle'],
  [/card__title/, 'card heading'],
  [/\bcard\b|\bstat\b/, 'stat/content card'],
  [/chart|an__chart/, 'chart'],
  [/quote/, 'quote'],
  [/slide__footer|slide__page/, 'footer'],
];
function describeSlideElement(doc, xPx, yPx) {
  try {
    let el = doc.elementFromPoint(xPx, yPx);
    if (!el || el === doc.body || el === doc.documentElement) return null;
    let role = null;
    let node = el;
    for (let hops = 0; node && node !== doc.body && hops < 6; node = node.parentElement, hops++) {
      const cls = (typeof node.className === 'string' ? node.className : (node.getAttribute && node.getAttribute('class'))) || '';
      const hit = SLIDE_ROLES.find(([re]) => re.test(cls));
      if (hit) { role = hit[1]; el = node; break; }
      const tag = (node.tagName || '').toLowerCase();
      if (/^h[1-3]$/.test(tag)) { role = 'heading'; el = node; break; }
      if (tag === 'li')  { role = 'bullet'; el = node; break; }
      if (tag === 'img') { role = 'image';  el = node; break; }
      if (tag === 'svg' || tag === 'canvas') { role = 'chart'; el = node; break; }
    }
    const text = (el.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 80);
    const label = role || 'element';
    return text ? `${label} "${text}"` : label;
  } catch { return null; }
}

function SlideHtmlFrame({ html, index, pins = [], pendingAnchor = null, activeAnchorId = null, onPlace, onPinClick, onPendingClear }) {
  const wrapRef = React.useRef(null);
  const iframeRef = React.useRef(null);
  // Outer CSS scale that shrinks the 1920×1080 iframe to the column
  // width. Start at 0 and measure synchronously in useLayoutEffect so
  // the FIRST painted frame is already at the right fit scale — without
  // this it paints once at scale(1) (a zoomed-in crop of the slide's
  // top-left corner), which flashes as a stray template when you jump
  // between slides.
  const [scale, setScale] = React.useState(0);
  // Reveal-gate keyed to the document currently shown. The iframe stays
  // hidden behind the neutral placeholder until the doc whose markup is
  // exactly `html` has fired load — so we never show a half-parsed frame
  // nor the PREVIOUS slide's document while a new srcDoc is still
  // navigating (the stale-artifact flash). Comparing against `html`
  // (not a boolean) flips back to hidden the instant the content
  // changes, synchronously, with no extra paint.
  const [loadedHtml, setLoadedHtml] = React.useState(null);
  const loaded = loadedHtml === html;
  React.useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const measure = (w) => { if (w > 0) setScale(Math.min(1, w / 1920)); };
    measure(el.getBoundingClientRect().width);
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(entries => {
      for (const e of entries) measure(e.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  // Click → normalised (0..1) coordinates on the 16:9 canvas. The
  // wrapper shares the slide's aspect ratio, so percentages map
  // straight through regardless of the iframe's CSS scale.
  const place = (e) => {
    if (!onPlace) return;
    const r = e.currentTarget.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (e.clientX - r.left) / r.width));
    const y = Math.max(0, Math.min(1, (e.clientY - r.top) / r.height));
    let el = null;
    try {
      const doc = iframeRef.current && iframeRef.current.contentDocument;
      if (doc) el = describeSlideElement(doc, x * 1920, y * 1080);
    } catch { /* cross-origin / not ready — coordinates still work */ }
    onPlace(x, y, el);
  };
  return (
    <div
      ref={wrapRef}
      style={{
        width: '100%',
        aspectRatio: '16 / 9',
        background: '#FCF8F7',  // matches --warm-grey-light so the iframe load flash isn't jarring
        borderRadius: 'var(--r-md)',
        overflow: 'hidden',
        position: 'relative',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
      }}
    >
      <iframe
        ref={iframeRef}
        title={`Slide ${index} preview`}
        srcDoc={html}
        onLoad={() => setLoadedHtml(html)}
        sandbox="allow-same-origin allow-scripts"  // allow-scripts lets slide-fit.js run inside the iframe
        style={{
          width: 1920,
          height: 1080,
          border: 0,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          position: 'absolute',
          top: 0,
          left: 0,
          opacity: loaded ? 1 : 0,  // hide until THIS doc has loaded — placeholder bg shows through
          transition: 'opacity 0.18s ease',
        }}
      />
      {/* Click-catcher — always armed so a single click on the slide
          drops a pin (the "click to add a circle" flow). Sits below
          the pin layer so existing pins stay clickable. */}
      {onPlace && (
        <div
          onClick={place}
          style={{ position: 'absolute', inset: 0, zIndex: 3, cursor: 'crosshair' }}
          title="Click to pin a comment to this spot"
        />
      )}
      {/* Pin layer — container ignores pointer events so it never
          blocks the iframe / catcher; each pin re-enables them. */}
      <div style={{ position: 'absolute', inset: 0, zIndex: 4, pointerEvents: 'none' }}>
        {pins.map(p => (
          <button
            key={p.id}
            type="button"
            className={`slide-pin${activeAnchorId === p.id ? ' slide-pin--active' : ''}`}
            style={{ left: `${p.x * 100}%`, top: `${p.y * 100}%` }}
            onClick={(e) => { e.stopPropagation(); onPinClick && onPinClick(p.id); }}
            title="Jump to this comment"
          >{p.number}</button>
        ))}
        {pendingAnchor && (
          <button
            type="button"
            className="slide-pin slide-pin--pending"
            style={{ left: `${pendingAnchor.x * 100}%`, top: `${pendingAnchor.y * 100}%` }}
            onClick={(e) => { e.stopPropagation(); onPendingClear && onPendingClear(); }}
            title="Remove this pin"
          >+</button>
        )}
      </div>
    </div>
  );
}

// Compact, non-interactive render of a slide's designed HTML for the
// thumbnail strip. Mirrors SlideHtmlFrame's 1920×1080 → column-width
// CSS-scale trick, but stripped of pins/click-catcher and gated behind
// pointer-events:none so the parent thumb button still owns the click
// (jump) and drag (reorder). Memoised on html so unrelated parent
// re-renders (active-index changes, polling, drag) never reload the
// iframe. aria-hidden + tabIndex=-1 keep it out of the a11y/tab order —
// the thumb's title attribute still carries the slide title for SR/hover.
const SlideThumbFrame = React.memo(function SlideThumbFrame({ html, index }) {
  const wrapRef = React.useRef(null);
  const [scale, setScale] = React.useState(0);
  const [loadedHtml, setLoadedHtml] = React.useState(null);
  const loaded = loadedHtml === html && scale > 0;
  React.useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const measure = (w) => { if (w > 0) setScale(Math.min(1, w / 1920)); };
    measure(el.getBoundingClientRect().width);
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(entries => {
      for (const e of entries) measure(e.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <span ref={wrapRef} className="deck-thumb__preview" aria-hidden="true">
      <iframe
        title={`Slide ${index} thumbnail`}
        srcDoc={html}
        scrolling="no"
        tabIndex={-1}
        onLoad={() => setLoadedHtml(html)}
        sandbox="allow-same-origin allow-scripts"  // allow-scripts lets slide-fit.js fit the 1920×1080 canvas
        style={{
          width: 1920,
          height: 1080,
          border: 0,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          position: 'absolute',
          top: 0,
          left: 0,
          pointerEvents: 'none',  // let clicks/drags fall through to the thumb button
          opacity: loaded ? 1 : 0,
          transition: 'opacity 0.18s ease',
        }}
      />
    </span>
  );
});

// Per-slide lifecycle state, derived from the slide row:
//   waiting      — row exists (storyline written) but not yet designed
//   in-progress  — initial design render in flight
//   running      — a user-triggered regenerate is in flight
//   generated    — designed (html_content present) but not signed off
//   checked      — the deck-reviewer QA pass examined it (reviewed_at set)
//   re-generated — regenerate completed; the slide was just refreshed
//   approved     — signed off
//   failed       — last render errored
const SLIDE_STATE = {
  waiting:        { label: 'Waiting',      icon: '○', bg: 'var(--bg-card)', fg: 'var(--fg-muted)' },
  'in-progress':  { label: 'In progress',  icon: '◐', bg: '#E6E0F4',        fg: '#3D2B6E' },
  running:        { label: 'Running',      icon: '◐', bg: '#E6E0F4',        fg: '#3D2B6E' },
  generated:      { label: 'Generated',    icon: '◆', bg: '#FFE7DC',        fg: '#B83C0F' },
  checked:        { label: 'Checked',      icon: '◉', bg: '#E6EEF8',        fg: '#1F5FA8' },
  're-generated': { label: 'Re-generated', icon: '↻', bg: '#FFE7DC',        fg: '#B83C0F' },
  approved:       { label: 'Approved',     icon: '✓', bg: '#DCF1E2',        fg: '#00702A' },
  failed:         { label: 'Failed',       icon: '✕', bg: '#FCE6E0',        fg: '#A20235' },
};
function slideState(s) {
  if (s.status === 'failed') return 'failed';
  if (s.status === 'regenerating') return 'running';
  if (s.status === 'rendering') return 'in-progress';
  if (s.approvedAt) return 'approved';
  if (s.status === 'regenerated') return 're-generated';
  if (s.reviewedAt && s.htmlContent) return 'checked';
  if (s.htmlContent) return 'generated';
  return 'waiting';
}

// Resolve a user id to a display label for the "who did what" trail.
// The caller themselves read as "You"; everyone else by name (falling
// back to a neutral label when the participant map doesn't carry them).
function whoLabel(userId, currentUserId, participants) {
  if (!userId) return 'Someone';
  if (userId === currentUserId) return 'You';
  return participants?.[userId]?.name || 'A collaborator';
}

function SlideCard({ slide, onComment, onEditComment, onDeleteComment, onRegenerate, onApprove, onUnapprove, onDeleteSlide, canDelete, busy, hideHeader, currentUserId, participants, canEdit = true, canManage = true }) {
  const [commentDraft, setCommentDraft] = React.useState('');
  const [showCommentPopup, setShowCommentPopup] = React.useState(false);
  const [popupDraft, setPopupDraft] = React.useState('');
  // Inline edit/delete state for the user's own still-open comments.
  // editingId holds the comment being edited (its body lives in
  // editDraft); confirmDeleteId gates the one-click delete behind a
  // Yes/No so a stray click doesn't drop feedback.
  const [editingId, setEditingId] = React.useState(null);
  const [editDraft, setEditDraft] = React.useState('');
  const [confirmDeleteId, setConfirmDeleteId] = React.useState(null);
  // Annotation state. pendingAnchor is the not-yet-saved pin (set by
  // clicking the slide); activeAnchorId syncs the highlight between a
  // circle and its comment row. Both reset on slide change because
  // SlideCard is keyed by slide index (remounts). submitting drives
  // immediate "Regenerating…" feedback — the regen does two model
  // calls + a PPTX rebuild (~1-2 min), so without it the button looks
  // inert and people re-click.
  const [pendingAnchor, setPendingAnchor] = React.useState(null);
  const [activeAnchorId, setActiveAnchorId] = React.useState(null);
  const [submitting, setSubmitting] = React.useState(false);
  // Confirm-before-delete dialog for this slide.
  const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);
  const openComments = (slide.comments || []).filter(c => !c.resolvedAt);
  const blocks = renderSlideMarkdown(slide.markdown || '');
  const isApproved = !!slide.approvedAt;
  const regenerating = slide.status === 'regenerating' || submitting;

  // Clear the local submitting flag once the parent action settles.
  React.useEffect(() => { if (!busy) setSubmitting(false); }, [busy]);

  // Open comments that carry a click-anchor become numbered orange
  // circles on the slide. Numbered in created order so a circle and
  // its comment row always share the same badge.
  const anchored = openComments.filter(c => c.anchorX != null && c.anchorY != null);
  const numberById = new Map(anchored.map((c, i) => [c.id, i + 1]));
  const pins = anchored.map(c => ({ id: c.id, x: c.anchorX, y: c.anchorY, number: numberById.get(c.id) }));

  // Scroll the highlighted comment into view when a circle is clicked.
  React.useEffect(() => {
    if (!activeAnchorId) return;
    const el = document.getElementById(`slide-comment-${activeAnchorId}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [activeAnchorId]);

  const submitComment = (e) => {
    e.preventDefault();
    const body = commentDraft.trim();
    if (!body) return;
    onComment(slide.index, body, pendingAnchor);
    setCommentDraft('');
    setPendingAnchor(null);
  };

  const doRegen = () => {
    setSubmitting(true);
    onRegenerate(slide.index, '');
    setPendingAnchor(null);
    setActiveAnchorId(null);
  };

  const submitPopupComment = (e) => {
    e.preventDefault();
    const body = popupDraft.trim();
    if (!body) return;
    onComment(slide.index, body, null);
    setPopupDraft('');
    setShowCommentPopup(false);
    setSubmitting(true);
    onRegenerate(slide.index, '');
    setPendingAnchor(null);
    setActiveAnchorId(null);
  };

  return (
    <div style={{
      border:'1px solid var(--border)',
      borderRadius:'var(--r-lg)',
      background:'var(--bg)',
      marginBottom:'var(--space-4)',
      overflow:'hidden',
    }}>
      {!hideHeader && (
        <div style={{
          display:'flex',alignItems:'center',gap:'var(--space-3)',
          padding:'var(--space-3) var(--space-5)',
          background:'var(--bg-alt)',
          borderBottom:'1px solid var(--border)',
        }}>
          <div style={{fontFamily:'var(--font-display)',fontSize:'var(--text-lg)'}}>
            Slide {slide.index} — {slide.title || '(untitled)'}
          </div>
          {slide.layout && (
            <span className="pill" style={{background:'var(--bg-card)',color:'var(--fg-muted)'}}>
              {slide.layout}
            </span>
          )}
          {(() => {
            const st = SLIDE_STATE[slideState(slide)];
            return (
              <span
                className="pill"
                style={{background:st.bg,color:st.fg,fontWeight:600}}
                title={st.label === 'Failed' && slide.error ? slide.error : st.label}
              >
                {st.icon} {st.label}
              </span>
            );
          })()}
          <div style={{flex:1}}/>
          <span style={{fontSize:'var(--text-xs)',color:'var(--fg-muted)'}}>
            {fmt(slide.updatedAt)}
          </span>
        </div>
      )}

      <div className="respo-sidebar">
        {/* Slide preview — when slide-designer has produced
            html_content, render it inside a sandboxed 1280×720
            iframe scaled to fit the available width via CSS
            transform. Falls back to the bullet-card markdown
            rendering when html_content is null (legacy slides
            from before slice 1 of the designer). */}
        <div className="block-pane block-pane--content" style={{padding:'var(--space-5)'}}>
          {slide.htmlContent ? (
            <>
              <SlideHtmlFrame
                html={slide.htmlContent}
                index={slide.index}
                pins={pins}
                pendingAnchor={pendingAnchor}
                activeAnchorId={activeAnchorId}
                onPlace={(x, y, el) => setPendingAnchor({ x, y, el })}
                onPinClick={(id) => setActiveAnchorId(id)}
                onPendingClear={() => setPendingAnchor(null)}
              />
              <div style={{marginTop:'var(--space-2)',fontSize:'var(--text-xs)',color: pendingAnchor ? 'var(--orange-deep, #E25620)' : 'var(--fg-muted)',display:'flex',alignItems:'center',gap:6}}>
                <span className="slide-pin slide-pin--inline" aria-hidden="true" style={{width:16,height:16,fontSize:10}}>{pendingAnchor ? '+' : '·'}</span>
                {pendingAnchor
                  ? `Pin placed${pendingAnchor.el ? ` on the ${pendingAnchor.el}` : ''} — write your comment on the right, then Add comment. (Click the pin to remove it.)`
                  : 'Tip: click anywhere on the slide to pin a comment to that spot — it shows the AI exactly what you mean. Optional.'}
              </div>
            </>
          ) : (
            blocks.map((b, i) => {
              if (b[0] === 'h')     return <h3 key={i} style={{marginBottom:'var(--space-2)'}}>{b[1]}</h3>;
              if (b[0] === 'kv')    return <div key={i} style={{fontSize:'var(--text-sm)',color:'var(--fg-muted)',marginBottom:'var(--space-2)'}}><strong style={{color:'var(--fg)'}}>{b[1]}:</strong> {b[2]}</div>;
              if (b[0] === 'p')     return <p key={i} style={{margin:'var(--space-2) 0'}}>{b[1]}</p>;
              if (b[0] === 'notes') return <div key={i} style={{marginTop:'var(--space-3)',padding:'var(--space-3)',background:'var(--bg-alt)',borderRadius:'var(--r-sm)',fontSize:'var(--text-sm)',color:'var(--fg-muted)',lineHeight:'var(--lh-normal)'}}><strong style={{color:'var(--fg)'}}>Speaker notes:</strong> {b[1]}</div>;
              if (b[0] === 'ul')    return <ul key={i} style={{margin:'var(--space-2) 0',paddingLeft:'var(--space-5)'}}>{b[1].map((it, j) => <li key={j} style={{marginBottom:'var(--space-1)'}}>{it}</li>)}</ul>;
              return null;
            })
          )}
        </div>

        {/* Comments rail — header + comment list scroll; the form and
            action buttons stay pinned below so they're always reachable
            even with a long comment thread. */}
        <div className="deck-comments-rail" style={{padding:'var(--space-4)',background:'var(--bg-alt)',display:'flex',flexDirection:'column',gap:'var(--space-3)'}}>
          <div style={{fontSize:'var(--text-xs)',letterSpacing:'0.04em',textTransform:'uppercase',color:'var(--fg-muted)',fontWeight:600,flexShrink:0}}>
            Comments {openComments.length > 0 ? `(${openComments.length} open)` : ''}
          </div>
          <div className="deck-comments-scroll" style={{display:'flex',flexDirection:'column',gap:'var(--space-2)'}}>
            {(slide.comments || []).length === 0 && (
              <div style={{fontSize:'var(--text-sm)',color:'var(--fg-muted)'}}>No comments yet. Add one to refine this slide.</div>
            )}
            {(slide.comments || []).map(c => {
              const num = numberById.get(c.id);
              const isActive = activeAnchorId === c.id;
              // Only the author can edit/delete, and only while the
              // comment is still open — once it's resolved (e.g. a
              // regenerate consumed it) it's frozen.
              const canModify = !c.resolvedAt && !!currentUserId && c.authorUserId === currentUserId;
              const isEditing = editingId === c.id;
              return (
                <div
                  key={c.id}
                  id={`slide-comment-${c.id}`}
                  onClick={() => !isEditing && num != null && setActiveAnchorId(c.id)}
                  className={isActive ? 'slide-comment--active' : undefined}
                  style={{
                    display:'flex',
                    gap:'var(--space-2)',
                    padding:'var(--space-2) var(--space-3)',
                    background:'var(--bg)',
                    border:'1px solid var(--border)',
                    borderRadius:'var(--r-sm)',
                    fontSize:'var(--text-sm)',
                    opacity: c.resolvedAt ? 0.6 : 1,
                    cursor: !isEditing && num != null ? 'pointer' : 'default',
                  }}
                >
                  {num != null && (
                    <span className="slide-pin slide-pin--inline" aria-hidden="true">{num}</span>
                  )}
                  <div style={{flex:1,minWidth:0}}>
                    {isEditing ? (
                      <div onClick={e => e.stopPropagation()} style={{display:'flex',flexDirection:'column',gap:'var(--space-2)'}}>
                        <textarea
                          className="textarea"
                          value={editDraft}
                          onChange={e => setEditDraft(e.target.value)}
                          rows={2}
                          autoFocus
                          style={{fontFamily:'inherit'}}
                        />
                        <div style={{display:'flex',gap:'var(--space-2)'}}>
                          <button
                            type="button"
                            className="btn-orange"
                            disabled={regenerating || !editDraft.trim()}
                            onClick={() => {
                              const t = editDraft.trim();
                              if (!t) return;
                              onEditComment(c.id, t);
                              setEditingId(null);
                              setEditDraft('');
                            }}
                            style={{padding:'4px 12px',fontSize:'var(--text-xs)'}}
                          >Save</button>
                          <button
                            type="button"
                            className="btn-ghost"
                            onClick={() => { setEditingId(null); setEditDraft(''); }}
                            style={{padding:'4px 12px',fontSize:'var(--text-xs)'}}
                          >Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div style={{textDecoration: c.resolvedAt ? 'line-through' : 'none'}}>{c.body}</div>
                        <div style={{fontSize:'var(--text-xs)',color:'var(--fg-muted)',marginTop:'var(--space-1)',display:'flex',alignItems:'center',gap:'var(--space-2)',flexWrap:'wrap'}}>
                          <span><strong style={{color:'var(--fg)',fontWeight:600}}>{whoLabel(c.authorUserId, currentUserId, participants)}</strong> · {fmt(c.createdAt)} · {c.resolvedAt ? 'resolved' : (num != null ? 'pinned' : 'open')}</span>
                          {canModify && (confirmDeleteId === c.id ? (
                            <span style={{display:'inline-flex',alignItems:'center',gap:'var(--space-1)'}}>
                              <span style={{color:'#A20235'}}>Delete?</span>
                              <button type="button" className="deck-comment-action deck-comment-action--danger" disabled={regenerating} onClick={e => { e.stopPropagation(); onDeleteComment(c.id); setConfirmDeleteId(null); }}>Yes</button>
                              <button type="button" className="deck-comment-action" onClick={e => { e.stopPropagation(); setConfirmDeleteId(null); }}>No</button>
                            </span>
                          ) : (
                            <span style={{display:'inline-flex',alignItems:'center',gap:'var(--space-2)'}}>
                              <button type="button" className="deck-comment-action" disabled={regenerating} onClick={e => { e.stopPropagation(); setConfirmDeleteId(null); setEditingId(c.id); setEditDraft(c.body); }}>Edit</button>
                              <button type="button" className="deck-comment-action deck-comment-action--danger" disabled={regenerating} onClick={e => { e.stopPropagation(); setConfirmDeleteId(c.id); }}>Delete</button>
                            </span>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {!canEdit && (
            <div style={{fontSize:'var(--text-sm)',color:'var(--fg-muted)',padding:'var(--space-2) 0',flexShrink:0}}>
              You're previewing this shared deck. <strong>Accept</strong> it (banner above) to comment, approve and regenerate slides.
            </div>
          )}
          {canEdit && (
          <form onSubmit={submitComment} style={{display:'flex',flexDirection:'column',gap:'var(--space-2)',flexShrink:0}}>
            <textarea
              className="textarea"
              value={commentDraft}
              onChange={e => setCommentDraft(e.target.value)}
              placeholder={pendingAnchor ? 'Describe the change for the pinned spot…' : 'What should change on this slide?'}
              rows={2}
              style={{fontFamily:'inherit'}}
            />
            <button type="submit" className="btn-ghost" disabled={regenerating || !commentDraft.trim()}>
              {pendingAnchor ? 'Add pinned comment' : 'Add comment'}
            </button>
          </form>
          )}
          {canEdit && (
          <div style={{borderTop:'1px solid var(--border)',paddingTop:'var(--space-3)',display:'flex',flexDirection:'column',gap:'var(--space-2)',flexShrink:0}}>
            {/* Regenerate. With open comments, the button fires
                immediately — the open comments are the rewrite brief.
                Without comments, the button looks greyed and a click
                opens a small popup to add a comment first. */}
            {openComments.length > 0 ? (
              <button
                type="button"
                className="btn-orange"
                onClick={doRegen}
                disabled={busy || regenerating}
                style={{width:'100%'}}
              >
                {regenerating ? 'Regenerating…' : `Regenerate from ${openComments.length} comment${openComments.length === 1 ? '' : 's'}`}
              </button>
            ) : (
              <div style={{position:'relative'}}>
                <button
                  type="button"
                  className="btn-orange"
                  onClick={() => !busy && !regenerating && setShowCommentPopup(true)}
                  aria-disabled={busy || regenerating}
                  title="Add a comment first to regenerate this slide"
                  style={{
                    width:'100%',
                    opacity: 0.5,
                    cursor: (busy || regenerating) ? 'not-allowed' : 'pointer',
                  }}
                >
                  {regenerating ? 'Regenerating…' : 'Regenerate slide'}
                </button>
                {showCommentPopup && (
                  <div
                    role="dialog"
                    aria-label="Add a comment before regenerating"
                    style={{
                      position:'absolute',
                      bottom:'calc(100% + var(--space-2))',
                      left:0,
                      right:0,
                      zIndex:10,
                      background:'var(--bg)',
                      border:'1px solid var(--border)',
                      borderRadius:'var(--r-md)',
                      boxShadow:'0 8px 24px rgba(0,0,0,0.12)',
                      padding:'var(--space-3)',
                    }}
                  >
                    <form onSubmit={submitPopupComment} style={{display:'flex',flexDirection:'column',gap:'var(--space-2)'}}>
                      <div style={{fontSize:'var(--text-xs)',color:'var(--fg-muted)'}}>
                        Add a comment so the AI knows what to change.
                      </div>
                      <textarea
                        className="textarea"
                        value={popupDraft}
                        onChange={e => setPopupDraft(e.target.value)}
                        placeholder="What should change on this slide?"
                        rows={3}
                        autoFocus
                        style={{fontFamily:'inherit'}}
                      />
                      <div style={{display:'flex',gap:'var(--space-2)',justifyContent:'flex-end'}}>
                        <button
                          type="button"
                          className="btn-ghost"
                          onClick={() => { setShowCommentPopup(false); setPopupDraft(''); }}
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="btn-orange"
                          disabled={!popupDraft.trim()}
                        >
                          Add &amp; regenerate
                        </button>
                      </div>
                    </form>
                  </div>
                )}
              </div>
            )}
            {/* Approve toggle — drives the export gate at the top
                of the page. Greying or unsetting unapproves the
                slide; any regenerate auto-clears it server-side. */}
            <button
              type="button"
              className={isApproved ? 'btn-orange' : 'btn-ghost'}
              onClick={() => isApproved ? onUnapprove(slide.index) : onApprove(slide.index)}
              disabled={regenerating}
              style={{
                width:'100%',
                background: isApproved ? '#00702A' : '#E3F5E9',
                borderColor: isApproved ? '#00702A' : '#A5D9B6',
                color: isApproved ? '#fff' : '#00702A',
              }}
              title={isApproved ? 'Click to mark this slide as needing changes' : 'Mark this slide as approved — export buttons enable once every slide is approved'}
            >
              {isApproved ? '✓ Approved — click to unapprove' : 'Approve this slide'}
            </button>
            {isApproved && slide.approvedByUserId && (
              <div style={{fontSize:'var(--text-xs)',color:'#00702A',textAlign:'center'}}>
                Approved by {whoLabel(slide.approvedByUserId, currentUserId, participants)}
              </div>
            )}
            {regenerating && (
              <div style={{fontSize:'var(--text-xs)',color:'var(--orange-deep, #E25620)',display:'flex',alignItems:'center',gap:6}}>
                <span className="slide-pin__spinner" aria-hidden="true"/>
                Re-writing and re-designing this slide — this usually takes a minute or two. You can keep working on other slides.
              </div>
            )}
            {onDeleteSlide && canManage && (
              <div style={{borderTop:'1px solid var(--border)',paddingTop:'var(--space-3)',display:'flex',justifyContent:'center'}}>
                <button
                  type="button"
                  className="deck-delete-link"
                  onClick={() => setShowDeleteDialog(true)}
                  disabled={busy || regenerating || !canDelete}
                  title={canDelete ? 'Delete this slide from the deck' : 'A deck needs at least one slide'}
                >Delete slide</button>
              </div>
            )}
          </div>
          )}
        </div>
      </div>

      {showDeleteDialog && (
        <div className="modal-mask" onClick={() => setShowDeleteDialog(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{maxWidth:420,position:'relative'}}>
            <h3>Delete this slide?</h3>
            <p style={{margin:'var(--space-3) 0 var(--space-5)',color:'var(--fg-muted)',fontSize:'var(--text-sm)',lineHeight:'var(--lh-normal)'}}>
              Slide {slide.index}{slide.title ? ` — ${slide.title}` : ''} will be removed and the remaining slides renumbered. The .pptx is rebuilt automatically. This can’t be undone.
            </p>
            <div style={{display:'flex',gap:'var(--space-2)',justifyContent:'flex-end'}}>
              <button type="button" className="btn-ghost" onClick={() => setShowDeleteDialog(false)}>Cancel</button>
              <button
                type="button"
                className="btn-orange"
                style={{background:'#A20235',borderColor:'#A20235'}}
                onClick={() => { onDeleteSlide(slide.index); setShowDeleteDialog(false); }}
              >Delete slide</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Status pill for a deck share row.
const SHARE_STATUS_META = {
  pending:  { label: 'Pending',  bg: '#FFE7DC', fg: '#B83C0F' },
  accepted: { label: 'Accepted', bg: '#DCF1E2', fg: '#00702A' },
  declined: { label: 'Declined', bg: '#FCE6E0', fg: '#A20235' },
};

/**
 * "Share with people" modal. Owner-only. Lists the firm directory as a
 * multi-select, shows who the deck is already shared with (+ their
 * accept/decline status), and revokes a share. Sharing sends each new
 * recipient an eOS notification with the deck title + a link to preview
 * and Accept / Decline.
 */
function SharePeoplePanel({ runId, mode, deckTitle, shares, participants, currentUserId, onClose, onChanged }) {
  const [people, setPeople] = React.useState(null);
  const [selected, setSelected] = React.useState(() => new Set());
  const [query, setQuery] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    listPeople()
      .then(list => { if (!cancelled) setPeople(list || []); })
      .catch(e => { if (!cancelled) setErr(e.message || String(e)); });
    return () => { cancelled = true; };
  }, []);

  // Map existing shares by recipient so we can show status inline and
  // skip re-selecting people already shared with.
  const shareByUser = new Map((shares || []).map(s => [s.userId, s]));

  const toggle = (id) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const doShare = async () => {
    const ids = [...selected];
    if (ids.length === 0) return;
    setBusy(true); setErr(null);
    try {
      await deckPost(mode, runId, { action: 'share', userIds: ids });
      setSelected(new Set());
      await onChanged();
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (userId) => {
    setBusy(true); setErr(null);
    try {
      await deckPost(mode, runId, { action: 'unshare', userId });
      await onChanged();
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const q = query.trim().toLowerCase();
  const candidates = (people || [])
    .filter(p => (p.id || p._id) !== currentUserId)
    .filter(p => !q || (p.name || '').toLowerCase().includes(q) || (p.title || '').toLowerCase().includes(q));

  return (
    <div className="modal-mask" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{maxWidth:520,position:'relative',maxHeight:'80vh',display:'flex',flexDirection:'column'}}>
        <h3 style={{marginBottom:'var(--space-1)'}}>Share this deck</h3>
        <p style={{margin:'0 0 var(--space-4)',color:'var(--fg-muted)',fontSize:'var(--text-sm)',lineHeight:'var(--lh-normal)'}}>
          Pick colleagues to share <strong>“{deckTitle}”</strong> with. Each gets a direct message from you with a link to preview it, then Accept to collaborate (it joins their run history and they can comment, approve and regenerate) or Decline.
        </p>

        {(shares || []).length > 0 && (
          <div style={{marginBottom:'var(--space-4)'}}>
            <div style={{fontSize:'var(--text-xs)',letterSpacing:'0.04em',textTransform:'uppercase',color:'var(--fg-muted)',fontWeight:600,marginBottom:'var(--space-2)'}}>
              Already shared with
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:'var(--space-1)'}}>
              {(shares || []).map(s => {
                const meta = SHARE_STATUS_META[s.status] || SHARE_STATUS_META.pending;
                return (
                  <div key={s.id} style={{display:'flex',alignItems:'center',gap:'var(--space-2)',fontSize:'var(--text-sm)'}}>
                    <span style={{flex:1,minWidth:0,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>
                      {participants?.[s.userId]?.name || 'A colleague'}
                    </span>
                    <span className="pill" style={{background:meta.bg,color:meta.fg,fontWeight:600}}>{meta.label}</span>
                    <button type="button" className="deck-comment-action deck-comment-action--danger" disabled={busy} onClick={() => revoke(s.userId)}>Remove</button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <input
          className="input"
          placeholder="Search people…"
          value={query}
          onChange={e => setQuery(e.target.value)}
          style={{marginBottom:'var(--space-2)'}}
        />
        <div style={{flex:1,overflowY:'auto',border:'1px solid var(--border)',borderRadius:'var(--r-sm)',minHeight:120}}>
          {people === null && <div style={{padding:'var(--space-3)',color:'var(--fg-muted)',fontSize:'var(--text-sm)'}}>Loading people…</div>}
          {people !== null && candidates.length === 0 && <div style={{padding:'var(--space-3)',color:'var(--fg-muted)',fontSize:'var(--text-sm)'}}>No matching people.</div>}
          {candidates.map(p => {
            const id = p.id || p._id;
            const existing = shareByUser.get(id);
            const checked = selected.has(id);
            return (
              <label key={id} style={{display:'flex',alignItems:'center',gap:'var(--space-2)',padding:'var(--space-2) var(--space-3)',borderBottom:'1px solid var(--border)',cursor: existing ? 'default' : 'pointer',opacity: existing ? 0.6 : 1}}>
                <input type="checkbox" checked={checked} disabled={!!existing || busy} onChange={() => toggle(id)} />
                <span style={{flex:1,minWidth:0}}>
                  <span style={{fontSize:'var(--text-sm)',fontWeight:500}}>{p.name}</span>
                  {p.title && <span style={{fontSize:'var(--text-xs)',color:'var(--fg-muted)',marginLeft:6}}>{p.title}</span>}
                </span>
                {existing && <span style={{fontSize:'var(--text-xs)',color:'var(--fg-muted)'}}>{SHARE_STATUS_META[existing.status]?.label}</span>}
              </label>
            );
          })}
        </div>

        {err && <div style={{marginTop:'var(--space-2)',color:'#A20235',fontSize:'var(--text-sm)'}}>{err}</div>}

        <div style={{display:'flex',gap:'var(--space-2)',justifyContent:'flex-end',marginTop:'var(--space-4)'}}>
          <button type="button" className="btn-ghost" onClick={onClose} disabled={busy}>Close</button>
          <button type="button" className="btn-orange" onClick={doShare} disabled={busy || selected.size === 0}>
            {busy ? 'Sharing…' : `Share with ${selected.size || ''} ${selected.size === 1 ? 'person' : 'people'}`.trim()}
          </button>
        </div>
      </div>
    </div>
  );
}

// Progress shown while a Slide-creator-2 deck composes (one-shot), so the user
// lands in the viewer and watches a process rather than a frozen modal. The
// stages are illustrative — the composer is a single call — but they mirror the
// shape of Creator 1's run so the two feel consistent.
function V2ComposingPanel() {
  const STAGES = [
    'Reading the brief…',
    'Shaping the storyline…',
    'Choosing templates & layouts…',
    'Writing each slide…',
    'Resolving cover images…',
    'Finishing the design…',
  ];
  const [i, setI] = React.useState(0);
  React.useEffect(() => {
    const t = setInterval(() => setI(x => Math.min(x + 1, STAGES.length - 1)), 4500);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="kpi" style={{ textAlign: 'center', padding: '40px 24px' }}>
      <div className="slide-pin__spinner" aria-hidden="true" style={{ margin: '0 auto 16px', width: 28, height: 28 }} />
      <div style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--text-lg, 18px)', marginBottom: 8 }}>Designing your deck…</div>
      <div style={{ color: 'var(--fg-muted)', fontSize: 13 }}>{STAGES[i]}</div>
      <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginTop: 16 }}>
        {STAGES.map((_, k) => <span key={k} style={{ width: 7, height: 7, borderRadius: 999, background: k <= i ? 'var(--orange, #FF6428)' : 'var(--border)' }} />)}
      </div>
    </div>
  );
}

export function DeckView({ subPath, mode = 'agent', agentName = null, runHref = null }) {
  // subPath shape: "<runId>/deck" (agent mode) or "<messageId>/deck"
  // (chat mode). Both modes use the same component — the URL helpers
  // above swap the API base.
  const id    = (subPath || '').split('/')[0] || '';
  const runId = id;  // legacy name still used throughout the component
  // Extract the agent id from the ?agent=<id> query string that
  // spawn-slide-deck appends to its viewer link. `explicitAgentId` is
  // null when the link pre-dates the query param; `agentId` then falls
  // back to the canonical SDC agent id for the empty-state polling.
  // Only used in agent-keyed mode; chat-artefacts viewers key by
  // messageId. The explicit id also drives the breadcrumb's parent
  // link — we only deep-link back to a specific agent's run when we
  // actually know which agent owns it.
  const explicitAgentId = React.useMemo(() => {
    if (mode === 'chat') return null;
    const queryIdx = (subPath || '').indexOf('?');
    if (queryIdx === -1) return null;
    const params = new URLSearchParams((subPath || '').slice(queryIdx + 1));
    return params.get('agent') || null;
  }, [subPath, mode]);
  const agentId = mode === 'chat' ? null : (explicitAgentId || DEFAULT_DECK_AGENT_ID);
  const v2 = mode === 'v2';   // md-first deck (Slide creator 2), rendered client-side
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState(null);
  // Single-slide regenerations run concurrently — each slide gates only
  // its own buttons, so several can be in flight at once instead of one
  // blocking the rest. `regenInFlight` is the set of slide indices whose
  // regenerate POST hasn't returned yet; `bulkBusy` covers the sequential
  // "Regenerate all pending" run (and locks export while it's underway).
  // The backend already isolates each regen: it touches only that slide's
  // row and uploads the rebuilt .pptx to a unique R2 key, so there's no
  // shared state for two to race on.
  const [regenInFlight, setRegenInFlight] = React.useState(() => new Set());
  const [bulkBusy, setBulkBusy] = React.useState(false);
  const anyBusy = regenInFlight.size > 0 || bulkBusy;
  // Progress for the bulk "Regenerate all pending" button. Lives at
  // the top-level so the hooks order stays stable across the early
  // returns below; the actual filter + handler are computed after
  // data lands.
  const [bulkProgress, setBulkProgress] = React.useState(null);
  // Public read-only share-link state. Lazy: only fetched when the
  // user clicks "Share". Stored URL is reset on every page open so
  // a previous session's modal doesn't auto-show.
  const [shareUrl, setShareUrl] = React.useState(null);
  const [shareBusy, setShareBusy] = React.useState(false);
  const [shareError, setShareError] = React.useState(null);
  const [shareCopied, setShareCopied] = React.useState(false);
  // Share-with-people (eOS) state — owner opens the picker modal; a
  // recipient sees a pending banner and Accepts / Declines. `declined`
  // flips to a terminal "you declined" screen (a reload would 404 once
  // the share is declined, so we don't refetch).
  const [sharePeopleOpen, setSharePeopleOpen] = React.useState(false);
  const [respondBusy, setRespondBusy] = React.useState(false);
  const [declined, setDeclined] = React.useState(false);
  // Carousel state — which slide is active. Persisted only in
  // memory (reset on page reload). Auto-clamped to the slide
  // count when data changes.
  const [activeIndex, setActiveIndex] = React.useState(0);
  // Manual (Figma-style) editor. `editing` is the slide index currently open
  // in the editor (null = closed); `editSaving` gates its Save button while
  // the save-manual-edits POST is in flight.
  const [editing, setEditing] = React.useState(null);
  const [editSaving, setEditSaving] = React.useState(false);
  // Slide indices hand-edited in this viewer session — so we can warn before an
  // AI regenerate (which re-derives from markdown) silently discards the edits.
  const manuallyEditedRef = React.useRef(new Set());
  // Pending regenerate awaiting the "this will discard manual edits" confirm.
  const [regenConfirm, setRegenConfirm] = React.useState(null); // { slideIndex, notes }
  // Close the editor when the user navigates to a different slide so it never
  // lingers over the wrong slide (the editor is keyed to a single index).
  React.useEffect(() => { setEditing(null); }, [activeIndex]);
  // Editable-PPTX prep progress. The first export converts each
  // designed slide into native shapes (one Claude pass per slide);
  // after that the layout is cached and export is instant.
  const [pptxError, setPptxError] = React.useState(null);
  // Export progress — drives the in-place button label + bar instead of
  // navigating to a separate export page. null = idle; otherwise
  // { phase, pct|null, label }. One per export kind.
  const [pptxProgress, setPptxProgress] = React.useState(null);
  const [pdfProgress, setPdfProgress] = React.useState(null);
  // Structural edits — delete / reorder lock the deck briefly while the
  // server renumbers + rebuilds the .pptx. `addBusy` is its own flag
  // because adding a slide also runs two model calls (draft + design)
  // and takes a minute, like a regenerate.
  const [structuralBusy, setStructuralBusy] = React.useState(false);
  const [addBusy, setAddBusy] = React.useState(false);
  const [addOpen, setAddOpen] = React.useState(false);
  const [addPrompt, setAddPrompt] = React.useState('');
  // Drag-to-reorder state for the thumbnail strip. dragIndex is the
  // thumbnail being dragged; dragOverIndex is the drop target under the
  // cursor (drives the insertion-line highlight). Both reset on drop/end.
  const [dragIndex, setDragIndex] = React.useState(null);
  const [dragOverIndex, setDragOverIndex] = React.useState(null);
  // Touch-drag reorder (mobile). HTML5 drag-and-drop is mouse-only, so on
  // touch we drive the same reorder ourselves: a short press-and-hold lifts
  // the thumbnail, then dragging a finger across the strip moves it. We
  // attach the touchmove/end listeners natively (passive:false) so we can
  // preventDefault and stop the strip scrolling mid-drag — React's synthetic
  // touch listeners are passive and can't. `touchDragIndex` drives the
  // "lifted" visual; the mutable details live in touchRef.
  const [touchDragIndex, setTouchDragIndex] = React.useState(null);
  const touchRef = React.useRef(null);            // { fromIndex, startX, startY, started, overIndex, timer }
  const stripElRef = React.useRef(null);
  const slideIdsRef = React.useRef([]);           // current slide ids, in order
  const touchMoveRef = React.useRef(null);        // latest native touchmove handler
  const touchEndRef = React.useRef(null);         // latest native touchend handler
  React.useEffect(() => { slideIdsRef.current = (data?.slides || []).map(s => s.id); }, [data]);

  // Keep the active thumbnail centered in the strip as the slide changes
  // (Prev/Next, clicking a thumb, regenerate jumps, etc.).
  const slideCount = (data?.slides || []).length;
  React.useEffect(() => {
    const strip = stripElRef.current;
    if (!strip || slideCount === 0) return;
    const idx = Math.max(0, Math.min(activeIndex, slideCount - 1));
    const thumb = strip.querySelector(`[data-thumb-index="${idx}"]`);
    if (!thumb) return;
    const stripRect = strip.getBoundingClientRect();
    const thumbRect = thumb.getBoundingClientRect();
    const delta = (thumbRect.left - stripRect.left) - (strip.clientWidth - thumbRect.width) / 2;
    const max = strip.scrollWidth - strip.clientWidth;
    const target = Math.max(0, Math.min(strip.scrollLeft + delta, max));
    strip.scrollTo({ left: target, behavior: 'smooth' });
  }, [activeIndex, slideCount]);

  // Auto-follow during generation: advance the viewer to each slide the moment
  // it is DESIGNED (gains htmlContent), so the deck visibly builds one slide at
  // a time instead of appearing all-at-once at the end. Two rules keep this
  // from fighting the user or scrolling wildly (the failure that got the old
  // "follow the newest render on EVERY poll" version disabled):
  //   1. We jump to the newest *designed* slide — the highest index that
  //      already has htmlContent — NEVER to the last slide index, which during
  //      a build is an as-yet-undesigned placeholder ("the end"). This is the
  //      "springt ans Ende" bug: following the trailing placeholder instead of
  //      the slide that was actually just generated.
  //   2. We only move activeIndex when that newest-designed index actually
  //      CHANGES (guarded by lastFollowedRef), so the smooth strip-scroll fires
  //      once per new slide — not on every poll — which is what scrolled wildly
  //      and crashed the page before.
  // The moment the user navigates manually (activeIndex no longer matches the
  // value we set) or opens the manual editor, we hand control back and stop.
  const followRef = React.useRef(true);
  const lastFollowedRef = React.useRef(-1);
  React.useEffect(() => {
    const list = data?.slides || [];
    if (list.length === 0) return;
    // User drove the carousel themselves (clicked a thumb / Prev-Next) → the
    // active slide no longer matches the one we last auto-set. Stop following.
    if (lastFollowedRef.current !== -1 && activeIndex !== lastFollowedRef.current) {
      followRef.current = false;
    }
    const st = data?.run?.status;
    const building = mode === 'agent' && st != null && st !== 'done' && st !== 'failed';
    if (!building || !followRef.current || editing != null) return;
    // Newest slide that has actually been DESIGNED (has htmlContent). Falls back
    // to the first slide until anything is designed, so we sit on slide 1 — not
    // a later, still-blank placeholder.
    let newestPos = 0;
    list.forEach((s, i) => { if ((s.htmlContent || '').length > 0) newestPos = i; });
    if (newestPos !== lastFollowedRef.current) {
      lastFollowedRef.current = newestPos;
      if (newestPos !== activeIndex) setActiveIndex(newestPos);
    }
  }, [data, mode, activeIndex, editing]);

  const reload = React.useCallback(async () => {
    if (!runId) return;
    setError(null);
    // md-first deck: render each spec client-side via the same bridge the
    // sandbox uses, and feed the SAME viewer chrome a normalised slide list.
    if (v2) {
      const deck = readV2Deck(runId);
      if (!deck) { setError('This deck is no longer available — it may have been cleared. Compose it again from the creator.'); setData({ run: { status: 'done', title: 'Slide deck' }, slides: [], viewer: {} }); return; }
      const title = deck.title || 'Slide deck';
      const lset = deck.variant === 'eve' ? 'eve' : 'eraneos';
      // Still composing (handed off from the creator before the deck exists):
      // show the build process, not an empty viewer. The compose effect below
      // fires the one-shot composition and rewrites this entry when it lands.
      if (deck.status === 'composing') { setData({ run: { status: 'composing', title }, slides: [], viewer: {} }); return; }
      if (deck.status === 'error') { setError(deck.error || 'Composition failed.'); setData({ run: { status: 'failed', title }, slides: [], viewer: {} }); return; }
      const slides = (deck.slides || []).map((s, i) => {
        let html = s.html || '';
        if (!html) { try { html = renderSlideV2({ ...s, deckTitle: title, groundImage: s.groundImage, logoSet: lset }); } catch { html = ''; } }
        return { id: `v2-${i}`, index: i, title: v2SlideTitle(s), layout: s.template, htmlContent: html, status: 'approved', approvedAt: Date.now(), comments: [] };
      });
      setData({ run: { status: 'done', title }, slides, viewer: { canEdit: true, isOwner: true } });
      return;
    }
    try {
      const res = await deckGet(mode, runId);
      setData(res);
    } catch (err) {
      setError(err.message || String(err));
    }
  }, [runId, v2, mode]);

  React.useEffect(() => { reload(); }, [reload]);

  // md-first composition. When the viewer is handed a still-"composing" entry,
  // run the one-shot composer HERE (so the user lands in the viewer immediately
  // and watches the process, like Creator 1) and rewrite the entry when done.
  const v2ComposeRef = React.useRef(null);
  React.useEffect(() => {
    if (!v2 || !runId) return;
    if (data?.run?.status !== 'composing') return;
    if (v2ComposeRef.current === runId) return;   // already composing this deck
    v2ComposeRef.current = runId;
    const entry = readV2Deck(runId);
    if (!entry || entry.status !== 'composing') return;
    (async () => {
      try {
        const deck = await composeDeckV2({ brief: entry.brief || '', count: entry.count || 10, imageMode: entry.imageMode, imageStyle: entry.imageStyle, variant: entry.variant });
        if (!deck || !Array.isArray(deck.slides) || deck.slides.length === 0) throw new Error('The composer returned no slides — try a longer or clearer brief.');
        const title = deck.title || entry.title || 'Slide deck';
        const lset = entry.variant === 'eve' ? 'eve' : 'eraneos';
        // Render each slide, then PERSIST as a real agent run so the deck gets the
        // full Creator-1 flow (comments, approve, share/invite, export, history).
        const persistSlides = deck.slides.map((s, i) => {
          let html = ''; try { html = renderSlideV2({ ...s, deckTitle: title, groundImage: s.groundImage, logoSet: lset }); } catch { html = ''; }
          return { index: i + 1, title: v2SlideTitle(s), layout: s.template, html, spec: { template: s.template, ground: s.ground, content: s.content, groundImage: s.groundImage } };
        });
        const res = await fetch('/api/slide-system/persist-deck', {
          method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ title, variant: entry.variant, agentId, slides: persistSlides }),
        });
        const j = await res.json().catch(() => ({}));
        if (res.ok && j.runId) {
          try { localStorage.removeItem(V2_DECK_PREFIX + runId); } catch { /* ignore */ }
          // Hand off to the SAME agent-mode viewer (full flow + history).
          window.location.hash = `#agents/${encodeURIComponent(j.agentId || agentId)}/${encodeURIComponent(j.runId)}/deck?agent=${encodeURIComponent(j.agentId || agentId)}`;
          return;
        }
        // Persist failed — fall back to the client-side deck so the work isn't lost.
        writeV2Deck(runId, { status: 'done', title, slides: deck.slides, variant: entry.variant });
      } catch (err) {
        writeV2Deck(runId, { ...entry, status: 'error', error: err.message || String(err) });
      }
      reload();
    })();
  }, [v2, runId, data?.run?.status, reload]);

  // Poll while any slide is regenerating, OR (agent mode) while the run
  // itself is still pending — so the surface flips from the progress
  // card to the viewer on its own the moment the run reaches `done`,
  // with no manual refresh.
  React.useEffect(() => {
    const regenerating = (data?.slides || []).some(s => s.status === 'regenerating');
    const st = data?.run?.status;
    const agentPending = mode === 'agent' && st != null && st !== 'done' && st !== 'failed';
    if (!regenerating && !agentPending) return undefined;
    const t = setInterval(reload, 4000);
    return () => clearInterval(t);
  }, [data?.slides, data?.run?.status, mode, reload]);

  // Breadcrumb: show the run's title (not the raw run-id UUID) and a
  // parent crumb that lands on a real page. Agent-mode decks live under
  // their agent — deep-link back to the run in the agent detail. We
  // prefer the ?agent=<id> link param, then the owning agent the GET
  // response carries (so links that pre-date the param still resolve
  // the run rather than dumping on the bare agents list); chat-mode
  // decks were produced inline in a chat.
  const parentAgentId = explicitAgentId || data?.run?.agentId || null;
  const runTitle = data?.run?.title || 'Slide deck';
  // When mounted under the agents route (agentName supplied), build the
  // full trail Agents › <agent> › <run> › Deck viewer (the "Agents" crumb
  // is already rendered by the nav, so it isn't repeated here). Otherwise
  // keep the legacy two-crumb trail for #runs and chat decks.
  useSubCrumbs(
    mode === 'chat'
      ? [{ label: 'Chat', href: 'chat' }, { label: runTitle, href: null }]
      : agentName
        ? [
            { label: agentName, href: parentAgentId ? `agents/${parentAgentId}` : 'agents' },
            { label: runTitle, href: runHref || (parentAgentId && runId ? `agents/${parentAgentId}/${runId}` : 'agents') },
            { label: 'Deck viewer', href: null },
          ]
        : [
            { label: 'Agents', href: parentAgentId && runId ? `agents/${parentAgentId}/${runId}` : 'agents' },
            { label: runTitle, href: null },
          ],
  );

  // Optimistically flip ONE slide's status in local state so the UI
  // (thumbnail pulse, status pill, the disabled approve button on THAT
  // slide) reflects an in-flight regenerate immediately — and the 4s
  // poller above starts following it — without waiting for the long
  // POST to return.
  const setSlideStatus = (slideIndex, status) =>
    setData(d => d ? { ...d, slides: (d.slides || []).map(s => s.index === slideIndex ? { ...s, status } : s) } : d);

  // Heavy action — a single-slide regenerate. Tracks only THIS slide as
  // in-flight (so its own buttons disable while other slides stay fully
  // actionable) and marks the slide regenerating up front. Two regens on
  // different slides can run at the same time; the per-slide guard below
  // still stops the same slide being regenerated twice at once.
  const regenerate = async (slideIndex, notes, targetLayout) => {
    if (!runId || regenInFlight.has(slideIndex)) return;
    setRegenInFlight(prev => new Set(prev).add(slideIndex));
    setSlideStatus(slideIndex, 'regenerating');
    // A regenerate re-derives the slide from its markdown, discarding any
    // manual edits — so this slide is no longer "manually edited" afterwards.
    manuallyEditedRef.current.delete(slideIndex);
    let errMsg = null;
    try {
      await deckPost(mode, runId, { action: 'regenerate', slideIndex, notes, targetLayout });
    } catch (err) {
      errMsg = err.message || String(err);
    }
    // A long regenerate can time out at the gateway yet still apply
    // server-side, so refresh from the server — the latest state is the
    // source of truth. reload() clears `error`, so re-assert any action
    // error AFTER it to keep it visible inline (not as a full-page
    // replacement — see the render guard below).
    await reload();
    if (errMsg) setError(errMsg);
    setRegenInFlight(prev => { const next = new Set(prev); next.delete(slideIndex); return next; });
  };

  // Persist hand-edits from the manual editor, then refresh + close it. The
  // server stores the serialised slide document straight into html_content
  // (so the viewer + exports pick it up) and snapshots a timeline version.
  const saveManualEdits = async (slideIndex, html) => {
    if (!runId || editSaving) return;
    // md-first deck: persist the edited HTML straight back onto the stored
    // spec slide (so the viewer + a later export pick it up), then reload.
    if (v2) {
      const deck = readV2Deck(runId);
      if (deck && deck.slides && deck.slides[slideIndex]) { deck.slides[slideIndex].html = html; writeV2Deck(runId, deck); }
      manuallyEditedRef.current.add(slideIndex);
      await reload();
      setEditing(null);
      return;
    }
    setEditSaving(true);
    let errMsg = null;
    try {
      await deckPost(mode, runId, { action: 'save-manual-edits', slideIndex, html });
    } catch (err) {
      errMsg = err.message || String(err);
    }
    await reload();
    setEditSaving(false);
    if (errMsg) { setError(errMsg); return; }
    manuallyEditedRef.current.add(slideIndex);
    setEditing(null);
  };

  // Regenerate guarded by the "this discards manual edits" warning. The
  // SlideCard routes its Regenerate button through here; a slide that was
  // hand-edited this session pops a confirm first.
  const requestRegenerate = (slideIndex, notes) => {
    if (manuallyEditedRef.current.has(slideIndex)) {
      setRegenConfirm({ slideIndex, notes });
      return;
    }
    regenerate(slideIndex, notes);
  };

  // "Change slide type" from the editor — an explicit AI redesign into the
  // chosen archetype. Closes the editor and regenerates with targetLayout.
  const changeSlideType = (slideIndex, targetLayout) => {
    setEditing(null);
    regenerate(slideIndex, '', targetLayout);
  };

  // Fetch a slide's first (original AI generation) or last recorded version's
  // HTML for the editor's recover/revert actions.
  const loadVersionHtml = React.useCallback(async (slideIndex, which) => {
    if (!runId) return null;
    try {
      const res = await fetch(`${deckBase(mode, runId)}?versionHtml=${which}&slideIndex=${slideIndex}`, { credentials: 'same-origin' });
      const j = await res.json().catch(() => ({}));
      return res.ok ? (j.html || null) : null;
    } catch { return null; }
  }, [runId, mode]);

  // Light actions — approve / unapprove / comment / resolve. Quick D1
  // writes that DON'T rebuild the deck, so they skip the in-flight
  // tracking entirely: you can approve or comment on any slide while
  // another slide is mid-regeneration.
  const lightAction = async (payload) => {
    if (!runId) return;
    let errMsg = null;
    try {
      await deckPost(mode, runId, payload);
    } catch (err) {
      errMsg = err.message || String(err);
    }
    await reload();
    if (errMsg) setError(errMsg);
  };

  // Recipient responds to a pending deck share. Accept → reload (the
  // viewer flips to full collaborator access and the run joins their run
  // history). Decline → show the terminal screen (no reload — access is
  // now revoked server-side).
  const respondShare = async (decision) => {
    if (!runId || respondBusy) return;
    setRespondBusy(true);
    let errMsg = null;
    try {
      await deckPost(mode, runId, { action: 'respond-share', decision });
    } catch (err) {
      errMsg = err.message || String(err);
    }
    setRespondBusy(false);
    if (errMsg) { setError(errMsg); return; }
    if (decision === 'accept') await reload();
    else setDeclined(true);
  };

  // Structural edits — delete a slide, reorder, or add a new one. Each
  // renumbers the deck server-side and rebuilds the .pptx, so they lock
  // the structural controls while in flight and refresh from the server
  // afterwards (the server is the source of truth — a slow add-slide can
  // time out at the gateway yet still land, so we always reload).
  const structuralAction = async (payload) => {
    if (!runId) return null;
    let errMsg = null;
    let res = null;
    try {
      res = await deckPost(mode, runId, payload);
    } catch (err) {
      errMsg = err.message || String(err);
    }
    await reload();
    if (errMsg) setError(errMsg);
    return res;
  };

  const deleteSlide = async (slideIndex) => {
    if (anyBusy || structuralBusy || addBusy) return;
    setStructuralBusy(true);
    try {
      await structuralAction({ action: 'delete-slide', slideIndex });
      // Step back one so we don't strand the viewer past the new end.
      setActiveIndex(i => Math.max(0, i - (slideIndex - 1 <= i ? 1 : 0)));
    } finally {
      setStructuralBusy(false);
    }
  };

  const reorderSlides = async (orderIds, keepActiveIndex) => {
    if (anyBusy || structuralBusy || addBusy) return;
    setStructuralBusy(true);
    try {
      await structuralAction({ action: 'reorder', order: orderIds });
      if (typeof keepActiveIndex === 'number') setActiveIndex(keepActiveIndex);
    } finally {
      setStructuralBusy(false);
    }
  };

  const addSlide = async (position, prompt) => {
    if (anyBusy || structuralBusy || addBusy || !prompt.trim()) return;
    setAddBusy(true);
    try {
      const res = await structuralAction({ action: 'add-slide', position, prompt: prompt.trim() });
      // Jump to the freshly inserted slide (1-based → 0-based).
      if (res?.slideIndex) setActiveIndex(res.slideIndex - 1);
      setAddOpen(false);
      setAddPrompt('');
    } finally {
      setAddBusy(false);
    }
  };

  // Shared reorder commit for both the desktop HTML5 drop and the mobile
  // touch drag: move the slide at visual index `from` to `to` and persist.
  // Keeps the moved slide active so its new "Slide N of M" number shows
  // straight away. reorderSlides() reloads from the server, which renumbers
  // every thumbnail and the nav counter automatically.
  const moveSlide = (from, to) => {
    if (from == null || to == null || from === to) return;
    const ids = [...slideIdsRef.current];
    if (from < 0 || from >= ids.length || to < 0 || to >= ids.length) return;
    const [moved] = ids.splice(from, 1);
    ids.splice(to, 0, moved);
    reorderSlides(ids, to);
  };

  // Native (non-passive) touch handlers, refreshed each render so they
  // capture the latest moveSlide closure. The proxies below forward to
  // these via refs; we attach them with { passive: false } so the
  // preventDefault during a drag actually stops the strip from scrolling.
  touchMoveRef.current = (e) => {
    const info = touchRef.current;
    if (!info) return;
    const t = e.touches[0];
    if (!t) return;
    if (!info.started) {
      // Finger moved before the press-and-hold fired → it's a scroll or a
      // tap, not a reorder. Cancel the lift and let the browser handle it.
      if (Math.abs(t.clientX - info.startX) > 10 || Math.abs(t.clientY - info.startY) > 10) {
        clearTimeout(info.timer);
        touchRef.current = null;
      }
      return;
    }
    e.preventDefault();  // we own the gesture now — don't scroll the strip
    const el = document.elementFromPoint(t.clientX, t.clientY);
    const thumb = el && el.closest ? el.closest('[data-thumb-index]') : null;
    if (thumb) {
      const idx = Number(thumb.getAttribute('data-thumb-index'));
      if (Number.isFinite(idx)) { info.overIndex = idx; setDragOverIndex(idx); }
    }
    // Edge auto-scroll so off-screen thumbnails are reachable mid-drag.
    const strip = stripElRef.current;
    if (strip) {
      const r = strip.getBoundingClientRect();
      if (t.clientX > r.right - 44) strip.scrollLeft += 14;
      else if (t.clientX < r.left + 44) strip.scrollLeft -= 14;
    }
  };
  touchEndRef.current = () => {
    const info = touchRef.current;
    if (!info) return;
    clearTimeout(info.timer);
    if (info.started) moveSlide(info.fromIndex, info.overIndex);
    touchRef.current = null;
    setTouchDragIndex(null);
    setDragOverIndex(null);
  };

  const touchMoveProxy = React.useCallback((e) => { if (touchMoveRef.current) touchMoveRef.current(e); }, []);
  const touchEndProxy  = React.useCallback(() => { if (touchEndRef.current) touchEndRef.current(); }, []);
  // Callback ref on the strip — attaches the native touch listeners when it
  // mounts, detaches when it unmounts (slides go away / page changes).
  const setStripRef = React.useCallback((node) => {
    const prev = stripElRef.current;
    if (prev) {
      prev.removeEventListener('touchmove', touchMoveProxy);
      prev.removeEventListener('touchend', touchEndProxy);
      prev.removeEventListener('touchcancel', touchEndProxy);
    }
    stripElRef.current = node || null;
    if (node) {
      node.addEventListener('touchmove', touchMoveProxy, { passive: false });
      node.addEventListener('touchend', touchEndProxy);
      node.addEventListener('touchcancel', touchEndProxy);
    }
  }, [touchMoveProxy, touchEndProxy]);

  // Run an export in place (no page change): consume the endpoint's NDJSON
  // progress stream, drive a live button label via setProgress, then either
  // auto-download the finished file (mode 'file') or open the print page in
  // a new tab (mode 'html' — the PDF fallback when server rendering is off).
  // `kind` is the download extension; `openHtmlInNewTab` is true for PDF.
  const runStreamingExport = async ({ url, kind, setProgress, openHtmlInNewTab }) => {
    setPptxError(null);
    const deckTitle = data?.run?.title || 'Slide deck';
    setProgress({ phase: 'start', pct: null, label: 'Preparing…' });
    try {
      const resp = await fetch(withStream(url), { credentials: 'same-origin' });
      if (!resp.ok || !resp.body) throw new Error(`export → ${resp.status}`);
      const reader = resp.body.getReader();
      const dec = new TextDecoder();
      let buf = '';
      let done = null; // { url, mode }
      const onEvent = (ev) => {
        if (ev.phase === 'error') throw new Error(ev.message || 'export failed');
        if (ev.phase === 'done') { done = { url: ev.url || null, mode: ev.mode || 'file' }; return; }
        const pct = (typeof ev.total === 'number' && ev.total > 0 && typeof ev.current === 'number')
          ? Math.max(0, Math.min(100, Math.round((ev.current / ev.total) * 100)))
          : null;
        setProgress({ phase: ev.phase, pct, label: ev.label || 'Working…' });
      };
      for (;;) {
        const { value, done: streamDone } = await reader.read();
        if (value) buf += dec.decode(value, { stream: true });
        let nl;
        while ((nl = buf.indexOf('\n')) >= 0) {
          const line = buf.slice(0, nl).trim();
          buf = buf.slice(nl + 1);
          if (line) onEvent(JSON.parse(line));
        }
        if (streamDone) { const tail = buf.trim(); if (tail) onEvent(JSON.parse(tail)); break; }
      }
      if (!done || !done.url) throw new Error('export finished without a file');
      // PDF fallback (no server render): open the print page in a new tab.
      if (done.mode === 'html') {
        if (openHtmlInNewTab) window.open(done.url, '_blank', 'noopener');
        else window.location.href = done.url;
        setProgress(null);
        return;
      }
      // Auto-download the finished file in place. R2 can be a beat behind
      // its own put() for a large deck (cover image + canvas screenshots),
      // so the immediate GET occasionally 500/404s — retry with backoff
      // instead of making the user click "again".
      setProgress({ phase: 'upload', pct: 100, label: 'Downloading…' });
      let fileResp = null;
      for (let attempt = 0; attempt < 4; attempt++) {
        if (attempt > 0) await new Promise(r => setTimeout(r, 600 * attempt));
        try {
          fileResp = await fetch(done.url, { credentials: 'same-origin' });
          if (fileResp.ok) break;
        } catch { /* network blip — retry */ }
        fileResp = null;
      }
      if (!fileResp) throw new Error('download failed after retries');
      const blob = await fileResp.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = `${deckTitle}.${kind}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(objUrl), 60000);
      setProgress(null);
    } catch (err) {
      setProgress(null);
      setPptxError((err && err.message) || 'export failed');
    }
  };

  // Download the EDITABLE .pptx (built server-side: each slide rendered 1:1
  // with native editable text overlaid). Streams build progress into the
  // button and auto-downloads on finish — no separate export page.
  const prepareAndExportPptx = () => {
    if (pptxProgress) return;
    runStreamingExport({ url: exportPptxUrl(mode, runId), kind: 'pptx', setProgress: setPptxProgress, openHtmlInNewTab: false });
  };

  // Export the full-bleed PDF the same in-place way. The document itself is
  // unchanged (server-rendered, one 16:9 page per slide); only the flow is
  // in-place now — progress in the button, then auto-download.
  const exportPdfInline = () => {
    if (pdfProgress) return;
    runStreamingExport({ url: exportPdfUrl(mode, runId), kind: 'pdf', setProgress: setPdfProgress, openHtmlInNewTab: true });
  };

  if (declined) return (
    <div className="page">
      <div className="page__head"><h1>Deck declined</h1></div>
      <div className="kpi" style={{textAlign:'center'}}>
        <div className="kpi__label">You declined this shared deck</div>
        <p style={{marginTop:'var(--space-2)',color:'var(--fg-muted)'}}>
          Nothing was changed. You can close this tab — the deck won't appear in your run history.
        </p>
      </div>
    </div>
  );
  if (!runId) return <div className="page"><h1>Deck viewer</h1><p>Missing run id.</p></div>;
  // Only blank the page when we have nothing to show. Once a deck has
  // loaded, a later action error (e.g. a slow regenerate) shows as an
  // inline banner instead of wiping the whole view.
  if (error && !data) return <div className="page"><h1>Deck viewer</h1><p style={{color:'#A20235'}}>{error}</p></div>;
  if (!data) return <div className="page"><h1>Deck viewer</h1><p>Loading…</p></div>;

  const slides = data.slides || [];
  // Viewer capabilities, derived from the GET response. Legacy / chat
  // responses without a richer `viewer` default to full edit + manage so
  // existing behaviour is unchanged.
  const viewer = data.viewer || {};
  const participants = data.participants || {};
  const canEdit = viewer.canEdit !== false;
  const canManage = viewer.isOwner || viewer.isAdmin || (viewer.role == null);
  const pendingShare = viewer.share && viewer.share.status === 'pending' ? viewer.share : null;
  const shareSenderName = pendingShare
    ? (participants[pendingShare.sharedByUserId]?.name || 'A colleague')
    : null;
  // Share-with-people is an agent-run feature (chat decks aren't runs).
  const canSharePeople = mode === 'agent' && canManage;

  // Stream slides into the viewer as soon as they're designed: the
  // carousel appears once the FIRST slide exists, with a "still
  // designing" banner covering the rest while the run keeps running.
  // Before any slide exists (briefing / storyline / drafter), this
  // surface is a PROGRESS CARD — the step trace + the briefing-approval
  // form — never an empty viewer.
  const runStatus = data.run?.status;
  const hasSlides = slides.length > 0;
  // Run still producing slides (agent mode only — chat decks have no
  // pollable multi-step run): keep the carousel's "still designing"
  // banner up and the poller running so new slides appear on their own.
  const stillBuilding = mode === 'agent' && runStatus != null && runStatus !== 'done' && runStatus !== 'failed';

  // Slides that have at least one open (unresolved) comment AND
  // aren't currently mid-regeneration. These are the candidates the
  // bulk "Regenerate all pending" action processes sequentially.
  const pendingSlides = slides.filter(s =>
    s.status !== 'regenerating' &&
    (s.comments || []).some(c => !c.resolvedAt),
  );

  const regenerateAllPending = async () => {
    if (pendingSlides.length === 0 || anyBusy) return;
    setBulkProgress({ done: 0, total: pendingSlides.length });
    setBulkBusy(true);
    try {
      // Process one slide at a time — each regen runs a model pass
      // server-side, so a sequential queue keeps load bounded and lets
      // partial progress survive a single failure.
      for (let i = 0; i < pendingSlides.length; i++) {
        try {
          await deckPost(mode, runId, {
            action: 'regenerate',
            slideIndex: pendingSlides[i].index,
            notes: '',
          });
        } catch (err) {
          // Surface but keep going — partial bulk completion is
          // better than aborting the queue on a single failure.
          setError(`Slide ${pendingSlides[i].index} regen failed: ${err.message || String(err)}`);
        }
        setBulkProgress({ done: i + 1, total: pendingSlides.length });
      }
      await reload();
    } finally {
      setBulkBusy(false);
      setBulkProgress(null);
    }
  };

  // ── md-first ("Slide creator 2") viewer ──────────────────────────
  // Same chrome as the agent deck (page head, thumbnail strip, prev/next,
  // the on-canvas SlideEditor) but no agent-only actions (comments,
  // approve, regenerate, server export, share) — those are meaningless for
  // a client-composed deck. Reuses the exact same presentational
  // components/classes so it looks 1:1 with Slide creator 1.
  if (v2) {
    const safeIdx = Math.max(0, Math.min(activeIndex, slides.length - 1));
    const active = slides[safeIdx];
    const composing = data.run?.status === 'composing';
    return (
      <div className="page">
        <div className="page__head">
          <h1>{data.run?.title || 'Slide deck'}</h1>
          <div className="lede">
            {composing
              ? 'Composing your deck on the brand system — designing the storyline and laying out each slide. It appears here as soon as it’s ready.'
              : slides.length
                ? 'Your deck — click a slide to edit it directly (move, resize, retype and re-lay-out elements, like Figma). Every slide stays inside the brand guardrails.'
                : 'No slides yet. Compose a deck from the creator.'}
          </div>
          {error && (
            <div style={{ marginTop: 'var(--space-3)', padding: '8px 12px', background: '#FCE6E0', color: '#A20235', borderRadius: 6, fontSize: 12.5 }} role="alert">{error}</div>
          )}
        </div>
        {composing && <V2ComposingPanel />}
        {active && (
          <>
            <div className="deck-carousel__strip" style={{ marginBottom: 'var(--space-3)' }}>
              {slides.map((s, i) => (
                <button
                  key={s.id}
                  type="button"
                  data-thumb-index={i}
                  onClick={() => setActiveIndex(i)}
                  className={`deck-thumb${i === safeIdx ? ' deck-thumb--active' : ''}`}
                  title={`Slide ${i + 1}${s.title ? ` — ${s.title}` : ''}`}
                >
                  <span className="deck-thumb__num">{i + 1}</span>
                  {s.htmlContent ? <SlideThumbFrame html={s.htmlContent} index={i + 1} /> : <span className="deck-thumb__title">{s.title || `Slide ${i + 1}`}</span>}
                </button>
              ))}
            </div>
            <div className="deck-carousel">
              <div className="deck-carousel__nav">
                <button type="button" className="btn-ghost" onClick={() => setActiveIndex(Math.max(0, safeIdx - 1))} disabled={safeIdx === 0} style={{ padding: '6px 14px', flexShrink: 0 }} aria-label="Previous slide">← Prev</button>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap', justifyContent: 'center', minWidth: 0, flex: 1 }}>
                  <span style={{ fontSize: 13, color: 'var(--fg-muted)', fontWeight: 600, whiteSpace: 'nowrap' }}>Slide {safeIdx + 1} of {slides.length}</span>
                  <span style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--text-md, 15px)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 280 }}>{active.title || '(untitled)'}</span>
                  {active.htmlContent && (
                    <button type="button" className={editing === active.index ? 'btn-orange' : 'btn-ghost'} onClick={() => setEditing(editing === active.index ? null : active.index)} style={{ padding: '6px 14px', flexShrink: 0 }} title="Hand-edit this slide directly, like Figma.">{editing === active.index ? '◧ View slide' : '✎ Edit manually'}</button>
                  )}
                </div>
                <button type="button" className="btn-ghost" onClick={() => setActiveIndex(Math.min(slides.length - 1, safeIdx + 1))} disabled={safeIdx === slides.length - 1} style={{ padding: '6px 14px', flexShrink: 0 }} aria-label="Next slide">Next →</button>
              </div>
              {editing === active.index && active.htmlContent ? (
                <SlideEditor
                  key={`v2-editor-${active.index}`}
                  html={active.htmlContent}
                  slideIndex={active.index}
                  currentLayout={active.layout}
                  busy={editSaving}
                  onSave={(html) => saveManualEdits(active.index, html)}
                  onCancel={() => setEditing(null)}
                  onChangeType={() => {}}
                  loadVersionHtml={async () => null}
                />
              ) : (
                <div className="deck-carousel__slide">
                  <SlideHtmlFrame html={active.htmlContent} index={safeIdx + 1} />
                </div>
              )}
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="page page--wide">
      <div className="page__head">
        <h1>{data.run?.title || 'Slide deck'}</h1>
        <div className="lede">
          {hasSlides
            ? 'Comment slide-by-slide and regenerate just the slides that need it. When every slide is approved, export the deck as PDF or PPTX.'
            : 'Your deck is being created. Follow the live progress below and confirm the briefing when prompted — slides appear here as they are designed.'}
        </div>
        {error && (
          <div style={{marginTop:'var(--space-3)',padding:'8px 12px',background:'#FCE6E0',color:'#A20235',borderRadius:6,fontSize:12.5,display:'flex',alignItems:'center',gap:'var(--space-2)'}} role="alert">
            <span style={{flex:1}}>{error}</span>
            <button type="button" className="btn-ghost" onClick={() => setError(null)} style={{padding:'3px 10px',fontSize:12}}>Dismiss</button>
          </div>
        )}
        {pendingShare && (
          <div style={{
            marginTop:'var(--space-3)',
            padding:'12px 16px',
            background:'#E6E0F4',
            border:'1px solid #B9A9E0',
            borderRadius:'var(--r-md)',
            display:'flex',alignItems:'center',gap:'var(--space-3)',flexWrap:'wrap',
          }} role="status">
            <div style={{flex:1,minWidth:200}}>
              <div style={{fontWeight:600,color:'#3D2B6E',fontSize:14}}>
                {shareSenderName} shared this deck with you
              </div>
              <div style={{fontSize:12.5,color:'#3D2B6E',marginTop:2}}>
                Preview it below. Accept to collaborate — it joins your run history and you can comment, approve and regenerate slides. Decline and nothing happens.
              </div>
            </div>
            <button
              type="button"
              className="btn-orange"
              disabled={respondBusy}
              onClick={() => respondShare('accept')}
              style={{background:'#00702A',borderColor:'#00702A'}}
            >{respondBusy ? 'Working…' : 'Accept'}</button>
            <button
              type="button"
              className="btn-ghost"
              disabled={respondBusy}
              onClick={() => respondShare('decline')}
            >Decline</button>
          </div>
        )}
        {pendingSlides.length > 0 && (
          <div style={{
            marginTop:'var(--space-3)',
            padding:'10px 14px',
            background:'#FFE7DC',
            border:'1px solid #F08200',
            borderRadius:'var(--r-md)',
            display:'flex',alignItems:'center',gap:'var(--space-3)',flexWrap:'wrap',
          }}>
            <span style={{fontSize:13,color:'#B83C0F',fontWeight:500}}>
              {bulkProgress
                ? `Regenerating ${bulkProgress.done} of ${bulkProgress.total}…`
                : `${pendingSlides.length} slide${pendingSlides.length === 1 ? '' : 's'} ${pendingSlides.length === 1 ? 'has' : 'have'} open comments`}
            </span>
            <button
              type="button"
              className="btn-orange"
              onClick={regenerateAllPending}
              disabled={anyBusy || bulkProgress !== null}
              style={{marginLeft:'auto'}}
            >
              {bulkProgress ? 'Working…' : `Regenerate all ${pendingSlides.length}`}
            </button>
          </div>
        )}
        {hasSlides && stillBuilding && (
          <div style={{
            marginTop:'var(--space-3)',
            padding:'10px 14px',
            background:'#E6E0F4',
            border:'1px solid #B9A9E0',
            borderRadius:'var(--r-md)',
            display:'flex',alignItems:'center',gap:'var(--space-2)',
            fontSize:13,color:'#3D2B6E',fontWeight:500,
          }} role="status">
            <span className="live-dot" style={{flexShrink:0}}/>
            Still designing the deck — {slides.length} slide{slides.length === 1 ? '' : 's'} ready
            {(() => {
              const checkedSoFar = slides.filter(s => !!s.reviewedAt || !!s.approvedAt).length;
              return checkedSoFar > 0 ? `, ${checkedSoFar} checked` : '';
            })()} so far. New slides appear here as they are built.
          </div>
        )}
        {hasSlides && (() => {
          const approvedCount = slides.filter(s => !!s.approvedAt).length;
          const allApproved = approvedCount === slides.length;
          // "Checked" = the deck-reviewer QA pass has examined the slide
          // (reviewed_at stamped). reviewed_at is never reset, and approval
          // only happens after a slide is checked, so an approved slide
          // counts as checked too — this is a cumulative review-progress
          // meter, distinct from the mutually-exclusive per-slide badge.
          const checkedCount = slides.filter(s => !!s.reviewedAt || !!s.approvedAt).length;
          const allChecked = checkedCount === slides.length;
          return (
          <div style={{
            marginTop:'var(--space-3)',
            display:'flex',alignItems:'center',gap:'var(--space-2)',flexWrap:'wrap',
            fontSize:13,
          }}>
            <span
              style={{fontWeight:600,padding:'2px 9px',borderRadius:999,background:'#E6EEF8',color:'#1F5FA8',whiteSpace:'nowrap'}}
              title="Slides examined by the deck-reviewer QA pass (vision + static lint + model review)."
            >
              ◉ {checkedCount} of {slides.length} checked
              {allChecked && <span style={{fontWeight:500}}> · review complete</span>}
            </span>
            <span style={{fontWeight:600, color: allApproved ? '#00702A' : 'var(--fg)', whiteSpace:'nowrap'}}>
              {approvedCount} of {slides.length} approved
              {allApproved
                ? <span style={{color:'#00702A',fontWeight:500}}> · ready to ship</span>
                : <span style={{color:'var(--fg-muted)',fontWeight:400}}> · approve all to unlock export</span>}
            </span>
            <div style={{flex:1,minWidth:8}}/>
            <a
              href={exportShellUrl({ src: previewUrl(mode, runId), kind: 'html', title: data.run?.title || 'Slide deck' })}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-ghost"
              title="Open a branded HTML rendering of the deck in a new tab. Re-renders from the canonical content store on every load — comments and regenerations flow through automatically with no PPTX rebuild needed."
            >Open HTML preview</a>
            {allApproved ? (
              <>
                <button
                  type="button"
                  className="btn-orange"
                  onClick={exportPdfInline}
                  disabled={anyBusy || !!pdfProgress}
                  title="Export a full-bleed PDF — one 16:9 page per slide, exactly as rendered in the viewer. Downloads automatically when ready."
                  style={pdfProgress ? { position: 'relative', overflow: 'hidden' } : undefined}
                >
                  {pdfProgress ? (
                    <>
                      {pdfProgress.pct != null && (
                        <span style={{
                          position: 'absolute', left: 0, top: 0, bottom: 0,
                          width: `${pdfProgress.pct}%`,
                          background: 'rgba(255,255,255,0.22)',
                          transition: 'width 0.3s ease', pointerEvents: 'none',
                        }} aria-hidden="true" />
                      )}
                      <span style={{ position: 'relative' }}>
                        Your export is generated…{pdfProgress.pct != null ? ` ${pdfProgress.pct}%` : ''}
                      </span>
                    </>
                  ) : 'Export PDF'}
                </button>
                <button
                  type="button"
                  className="btn-orange"
                  onClick={prepareAndExportPptx}
                  disabled={anyBusy || !!pptxProgress}
                  title="Export a 1:1 PowerPoint — each slide matches the PDF exactly, with the titles and body as native, editable text boxes over the rendered design."
                  style={pptxProgress ? { position: 'relative', overflow: 'hidden' } : undefined}
                >
                  {pptxProgress ? (
                    <>
                      {pptxProgress.pct != null && (
                        <span style={{
                          position: 'absolute', left: 0, top: 0, bottom: 0,
                          width: `${pptxProgress.pct}%`,
                          background: 'rgba(255,255,255,0.22)',
                          transition: 'width 0.3s ease', pointerEvents: 'none',
                        }} aria-hidden="true" />
                      )}
                      <span style={{ position: 'relative' }}>
                        Your export is generated…{pptxProgress.pct != null ? ` ${pptxProgress.pct}%` : ''}
                      </span>
                    </>
                  ) : 'Export PPTX'}
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="btn-ghost"
                  disabled
                  title={`Approve all ${slides.length} slides to enable export. ${approvedCount} done so far.`}
                  style={{opacity:0.55,cursor:'not-allowed'}}
                >Export PDF (locked)</button>
                <button
                  type="button"
                  className="btn-ghost"
                  disabled
                  title={`Approve all ${slides.length} slides to enable export. ${approvedCount} done so far.`}
                  style={{opacity:0.55,cursor:'not-allowed'}}
                >Export PPTX (locked)</button>
              </>
            )}
            {canSharePeople && (
              <button
                type="button"
                className="btn-ghost"
                onClick={() => setSharePeopleOpen(true)}
                title="Share this deck with colleagues on eOS. They get a direct message from you with a link to preview it, then Accept to collaborate — comment, approve and regenerate slides — or Decline."
              >
                Share with people
                {(data.shares || []).length > 0 ? ` (${(data.shares || []).length})` : ''}
              </button>
            )}
            {canManage && (
            <button
              type="button"
              className="btn-ghost"
              disabled={shareBusy}
              title="Mint a public read-only URL anyone can open without an Eraneos login. The link can be revoked at any time."
              onClick={async () => {
                setShareError(null);
                setShareBusy(true);
                try {
                  const res = await fetch('/api/share-tokens', {
                    method: 'POST',
                    credentials: 'same-origin',
                    headers: { 'content-type': 'application/json' },
                    body: JSON.stringify({
                      target: mode === 'chat' ? 'chat-message' : 'agent-run',
                      id: runId,
                      kind: 'deck',
                    }),
                  });
                  const j = await res.json().catch(() => ({}));
                  if (!res.ok) throw new Error(j.error || `share → ${res.status}`);
                  // Build the absolute URL so it's copy-pasteable.
                  setShareUrl(new URL(j.url, publicOrigin()).toString());
                  setShareCopied(false);
                } catch (err) {
                  setShareError(err.message || String(err));
                } finally {
                  setShareBusy(false);
                }
              }}
            >{shareBusy ? 'Generating…' : 'Share read-only link'}</button>
            )}
          </div>
          );
        })()}
        {shareError && (
          <div style={{marginTop:'var(--space-2)',padding:'8px 12px',background:'#FCE6E0',color:'#A20235',borderRadius:6,fontSize:12.5}}>
            Couldn't create share link: {shareError}
          </div>
        )}
        {pptxError && (
          <div style={{marginTop:'var(--space-2)',padding:'8px 12px',background:'#FCE6E0',color:'#A20235',borderRadius:6,fontSize:12.5}}>
            Editable PPTX prep hit a snag: {pptxError}. Try Export PPTX again — finished slides are cached, so it resumes where it left off.
          </div>
        )}
        {shareUrl && (
          <div
            style={{
              marginTop:'var(--space-3)',padding:'10px 14px',
              background:'var(--bg-alt)',
              border:'1px solid var(--border)',borderRadius:'var(--r-md)',
              display:'flex',alignItems:'center',gap:'var(--space-2)',flexWrap:'wrap',
            }}
            role="status"
          >
            <span style={{fontSize:12,color:'var(--fg-muted)',whiteSpace:'nowrap'}}>Read-only share URL:</span>
            <code style={{flex:'1 1 240px',fontSize:12,padding:'4px 8px',background:'var(--bg)',borderRadius:4,wordBreak:'break-all'}}>
              {shareUrl}
            </code>
            <button
              type="button"
              className="btn-orange"
              onClick={async () => {
                try { await navigator.clipboard.writeText(shareUrl); setShareCopied(true); }
                catch { /* clipboard blocked — user can still select the code block */ }
              }}
              style={{padding:'5px 12px',fontSize:12}}
            >{shareCopied ? 'Copied ✓' : 'Copy'}</button>
            <button
              type="button"
              className="btn-ghost"
              onClick={() => { setShareUrl(null); setShareCopied(false); }}
              style={{padding:'5px 10px',fontSize:12}}
            >Dismiss</button>
          </div>
        )}
      </div>

      {!hasSlides && mode === 'agent' && agentId && (
        <PreSlidesPanel runId={runId} agentId={agentId}/>
      )}
      {!hasSlides && (mode !== 'agent' || !agentId) && (
        <div className="kpi" style={{textAlign:'center'}}>
          <div className="kpi__label">No slides persisted yet</div>
          <p style={{marginTop:'var(--space-2)',color:'var(--fg-muted)'}}>
            This run hasn't produced a deck. Wait for the run to finish, or open the run page from the agent detail view.
          </p>
        </div>
      )}

      {hasSlides && (() => {
        // Clamp activeIndex to the slide count whenever slides
        // change (e.g. after a bulk regenerate that didn't drop
        // any slides but updated content).
        const safeIdx = Math.max(0, Math.min(activeIndex, slides.length - 1));
        const active = slides[safeIdx];
        const goPrev = () => setActiveIndex(Math.max(0, safeIdx - 1));
        const goNext = () => setActiveIndex(Math.min(slides.length - 1, safeIdx + 1));
        const activeSt = SLIDE_STATE[slideState(active)];
        // Structural controls (add / delete / drag-reorder) lock while any
        // regenerate, bulk run, or structural edit is in flight.
        const structLocked = anyBusy || structuralBusy || addBusy;
        // Compute the new id ordering after dropping the dragged thumbnail
        // onto target, then persist it. We pass `target` back as the
        // active index so the moved slide stays in view.
        const handleDrop = (target) => {
          const from = dragIndex;
          setDragIndex(null);
          setDragOverIndex(null);
          moveSlide(from, target);
        };
        // Mobile: press-and-hold a thumbnail to lift it, then drag to
        // reorder. The move/end of the gesture run on the native listeners
        // attached via setStripRef (so they can block the strip scroll).
        const onThumbTouchStart = (i, e) => {
          if (structLocked || !canManage) return;
          const t = e.touches[0];
          if (!t) return;
          const timer = setTimeout(() => {
            const info = touchRef.current;
            if (!info) return;
            info.started = true;
            info.overIndex = info.fromIndex;
            setTouchDragIndex(i);
            setDragOverIndex(i);
            if (navigator.vibrate) { try { navigator.vibrate(12); } catch { /* noop */ } }
          }, 220);
          touchRef.current = { fromIndex: i, startX: t.clientX, startY: t.clientY, started: false, overIndex: i, timer };
        };
        return (
          <>
            {/* Reorder hint — the strip below is the drag surface. Adding a
                slide is the round + at the end of the strip; deleting a
                slide lives in the per-slide panel below. */}
            <div className="deck-tools">
              {canManage && <span className="deck-tools__hint" aria-hidden="true">⠿ Drag to reorder — press &amp; hold on touch</span>}
              <div style={{flex:1}}/>
              {structuralBusy && (
                <span className="deck-tools__busy">
                  <span className="slide-pin__spinner" aria-hidden="true"/> Updating deck…
                </span>
              )}
            </div>

            {addOpen && (
              <div className="deck-add">
                <div style={{fontSize:13,color:'var(--fg-muted)'}}>
                  Describe the new slide — it's inserted after slide {safeIdx + 1}, then drafted and designed automatically against the deck's brand.
                </div>
                <textarea
                  className="textarea"
                  value={addPrompt}
                  onChange={e => setAddPrompt(e.target.value)}
                  placeholder={'e.g. "A market-sizing slide with TAM, SAM and SOM as three stat cards"'}
                  rows={3}
                  disabled={addBusy}
                  style={{fontFamily:'inherit'}}
                  autoFocus
                />
                <div style={{display:'flex',alignItems:'center',gap:'var(--space-2)',flexWrap:'wrap'}}>
                  <button
                    type="button"
                    className="btn-orange"
                    disabled={addBusy || !addPrompt.trim()}
                    onClick={() => addSlide(active.index + 1, addPrompt)}
                  >{addBusy ? 'Generating…' : 'Generate & insert'}</button>
                  <button type="button" className="btn-ghost" disabled={addBusy} onClick={() => { setAddOpen(false); setAddPrompt(''); }}>Cancel</button>
                  {addBusy && (
                    <span style={{fontSize:'var(--text-xs)',color:'var(--orange-deep, #E25620)',display:'inline-flex',alignItems:'center',gap:6}}>
                      <span className="slide-pin__spinner" aria-hidden="true"/>
                      Drafting and designing the new slide — this usually takes a minute.
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Slide slider — thumbnail strip moved to the top so the
                active slide + its nav sit together below it. Click to
                jump; drag to reorder. Active thumb has an orange ring,
                approved thumbs a green dot, regenerating thumbs pulse. */}
            <div className="deck-carousel__strip" ref={setStripRef} style={{marginBottom:'var(--space-3)'}}>
              {slides.map((s, i) => {
                const isActive = i === safeIdx;
                const isApproved = !!s.approvedAt;
                const hasOpen = (s.comments || []).some(c => !c.resolvedAt);
                const st = SLIDE_STATE[slideState(s)];
                const isDragging = dragIndex === i || touchDragIndex === i;
                const isDragOver = dragOverIndex === i && (dragIndex != null || touchDragIndex != null) && dragOverIndex !== (dragIndex ?? touchDragIndex);
                return (
                  <button
                    key={s.id}
                    type="button"
                    data-thumb-index={i}
                    draggable={!structLocked && canManage}
                    onClick={() => setActiveIndex(i)}
                    onDragStart={(e) => { if (structLocked) return; setDragIndex(i); try { e.dataTransfer.effectAllowed = 'move'; } catch { /* noop */ } }}
                    onDragOver={(e) => { if (dragIndex == null) return; e.preventDefault(); setDragOverIndex(i); }}
                    onDrop={(e) => { e.preventDefault(); handleDrop(i); }}
                    onDragEnd={() => { setDragIndex(null); setDragOverIndex(null); }}
                    onTouchStart={(e) => onThumbTouchStart(i, e)}
                    className={`deck-thumb${isActive ? ' deck-thumb--active' : ''}${isApproved ? ' deck-thumb--approved' : ''}${isDragging ? ' deck-thumb--dragging' : ''}${isDragOver ? ' deck-thumb--dragover' : ''}`}
                    title={`Slide ${i + 1}${s.title ? ` — ${s.title}` : ''} · ${st.label}${hasOpen ? ' · has comments' : ''} · drag to reorder`}
                  >
                    <span className="deck-thumb__num">{i + 1}</span>
                    {s.htmlContent
                      ? <SlideThumbFrame html={s.htmlContent} index={i + 1} />
                      : <span className="deck-thumb__title">{s.title || `Slide ${i + 1}`}</span>}
                    <span className="deck-thumb__statusline" style={{color:st.fg}}>
                      <span aria-hidden="true">{st.icon}</span> {st.label}
                    </span>
                    {s.status === 'regenerating' && <span className="deck-thumb__pulse" aria-hidden="true"/>}
                    {hasOpen && <span className="deck-thumb__dot" aria-label="has open comments"/>}
                  </button>
                );
              })}
              {canManage && (
                <button
                  type="button"
                  className="deck-thumb-add-round"
                  onClick={() => setAddOpen(o => !o)}
                  disabled={structLocked}
                  title="Add a new slide from a prompt"
                  aria-label="Add slide"
                >+</button>
              )}
            </div>

            <div className="deck-carousel">
              {/* Nav row — sits directly above the slide in the same
                  container. Carries prev/next, the counter, the slide
                  title, and the live status (moved here from the card
                  header to save vertical space). */}
              <div className="deck-carousel__nav">
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={goPrev}
                  disabled={safeIdx === 0}
                  style={{padding:'6px 14px',flexShrink:0}}
                  aria-label="Previous slide"
                >← Prev</button>
                <div style={{display:'flex',alignItems:'center',gap:'var(--space-2)',flexWrap:'wrap',justifyContent:'center',minWidth:0,flex:1}}>
                  <span style={{fontSize:13,color:'var(--fg-muted)',fontWeight:600,whiteSpace:'nowrap'}}>
                    Slide {safeIdx + 1} of {slides.length}
                  </span>
                  <span style={{fontFamily:'var(--font-display)',fontSize:'var(--text-md, 15px)',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',maxWidth:280}}>
                    {active.title || '(untitled)'}
                  </span>
                  <span className="pill" style={{background:activeSt.bg,color:activeSt.fg,fontWeight:600}} title={activeSt.label === 'Failed' && active.error ? active.error : activeSt.label}>
                    {activeSt.icon} {activeSt.label}
                  </span>
                  {canEdit && active.htmlContent && (
                    <button
                      type="button"
                      className={editing === active.index ? 'btn-orange' : 'btn-ghost'}
                      onClick={() => setEditing(editing === active.index ? null : active.index)}
                      disabled={editSaving || regenInFlight.has(active.index)}
                      style={{padding:'6px 14px',flexShrink:0}}
                      title="Hand-edit this slide — move, resize, retype and re-lay-out elements directly, like Figma."
                    >{editing === active.index ? '◧ View slide' : '✎ Edit manually'}</button>
                  )}
                </div>
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={goNext}
                  disabled={safeIdx === slides.length - 1}
                  style={{padding:'6px 14px',flexShrink:0}}
                  aria-label="Next slide"
                >Next →</button>
              </div>

              {/* Active slide — either the manual editor (Figma-style direct
                  manipulation) or the full review card with its comments rail
                  (header hidden; its info now lives in the nav row above). */}
              {editing === active.index && active.htmlContent ? (
                <SlideEditor
                  key={`editor-${active.index}`}
                  html={active.htmlContent}
                  slideIndex={active.index}
                  currentLayout={active.layout}
                  busy={editSaving || regenInFlight.has(active.index)}
                  onSave={(html) => saveManualEdits(active.index, html)}
                  onCancel={() => setEditing(null)}
                  onChangeType={(layout) => changeSlideType(active.index, layout)}
                  loadVersionHtml={(which) => loadVersionHtml(active.index, which)}
                />
              ) : (
              <SlideCard
                key={active.index}
                slide={active}
                busy={regenInFlight.has(active.index) || bulkBusy || structuralBusy || addBusy}
                hideHeader
                currentUserId={data.viewer?.id}
                participants={participants}
                canEdit={canEdit}
                canManage={canManage}
                canDelete={slides.length > 1}
                onComment={(slideIndex, body, anchor) => lightAction({ action:'comment', slideIndex, body, anchorX: anchor?.x, anchorY: anchor?.y, anchorEl: anchor?.el })}
                onEditComment={(commentId, body) => lightAction({ action:'edit-comment', commentId, body })}
                onDeleteComment={(commentId)     => lightAction({ action:'delete-comment', commentId })}
                onRegenerate={(slideIndex, notes) => requestRegenerate(slideIndex, notes)}
                onApprove={(slideIndex)          => lightAction({ action:'approve',    slideIndex })}
                onUnapprove={(slideIndex)        => lightAction({ action:'unapprove',  slideIndex })}
                onDeleteSlide={(slideIndex)      => deleteSlide(slideIndex)}
              />
              )}
            </div>
          </>
        );
      })()}

      {sharePeopleOpen && (
        <SharePeoplePanel
          runId={runId}
          mode={mode}
          deckTitle={data.run?.title || 'Slide deck'}
          shares={data.shares || []}
          participants={participants}
          currentUserId={data.viewer?.id}
          onClose={() => setSharePeopleOpen(false)}
          onChanged={reload}
        />
      )}

      {regenConfirm && (
        <div className="modal-mask" onClick={() => setRegenConfirm(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{maxWidth:440}}>
            <h3>Regenerate over your manual edits?</h3>
            <p style={{margin:'var(--space-3) 0 var(--space-5)',color:'var(--fg-muted)',fontSize:'var(--text-sm)',lineHeight:'var(--lh-normal)'}}>
              You hand-edited slide {regenConfirm.slideIndex} in the editor. Regenerating rebuilds it from the slide’s text and <strong>discards those manual edits</strong>. They stay in the slide’s timeline — open the editor and choose <em>First generation</em> / <em>Last AI version</em> to get an edited version back.
            </p>
            <div style={{display:'flex',gap:'var(--space-2)',justifyContent:'flex-end'}}>
              <button type="button" className="btn-ghost" onClick={() => setRegenConfirm(null)}>Keep my edits</button>
              <button
                type="button"
                className="btn-orange"
                onClick={() => { const c = regenConfirm; setRegenConfirm(null); regenerate(c.slideIndex, c.notes); }}
              >Regenerate anyway</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
